// ViewChild/ElementRef went with the hidden PayU form — it was the only thing on
// this page that needed a DOM handle. Razorpay's modal is opened by its own SDK.
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { DataLayerService } from '../../services/datalayer.service';
import { CustomerAddress, ShippingMethod } from '../../models/order.model';
import { buildPdpUrl } from '../../models/wallpaper.model';
import { exclusiveMoney, roundMoney, roundTotal } from '../../utils/money.util';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  // The order summary card's few rules Tailwind's CDN can't express: the
  // expandable item rows (<details>) and a gentle entrance.
  styles: [`
    .os-card { animation: os-in .35s ease-out both; }
    @keyframes os-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
    .os-label { font-size: 10px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #a07a2c; }
    .os-line > summary { list-style: none; }
    .os-line > summary::-webkit-details-marker { display: none; }
    .os-chev { flex-shrink: 0; color: #9ca3af; transition: transform .2s ease, color .2s ease; }
    .os-line[open] .os-chev { transform: rotate(180deg); color: #600b0b; }
    .os-line > summary:hover .os-chev { color: #600b0b; }
    .os-thumb { height: 44px; width: 44px; flex-shrink: 0; overflow: hidden; border-radius: 10px; border: 1px solid #ecdcbc; background: #fff; }
    .os-thumb-icon { display: flex; align-items: center; justify-content: center; background: #fbf1df; color: #8a2a1f; }
    .os-detail { margin: 10px 0 0 56px; padding: 8px 12px; border-radius: 10px; background: #fbf6ee; border: 1px solid #f1e6d0; animation: os-in .2s ease-out both; }
    .os-row { display: flex; justify-content: space-between; gap: 12px; font-size: 11.5px; color: #6b7280; padding: 2px 0; font-variant-numeric: tabular-nums; }
    .os-row > span:last-child { color: #374151; text-align: right; }
    .os-row em { font-style: normal; font-size: 10px; color: #9ca3af; }
    .os-row-strong { margin-top: 4px; padding-top: 6px; border-top: 1px dashed #e3cfa6; color: #374151; font-weight: 500; }
    .os-row-strong > span:last-child { color: #111827; font-weight: 600; }
    .os-row-green, .os-row-green > span:last-child { color: #15803d; }
    @media (max-width: 380px) { .os-detail { margin-left: 0; } }
  `]
})
export class CheckoutComponent implements OnInit, OnDestroy {
  currentStep = 1;
  cartItems: any[] = [];
  private sub = new Subscription();

  // Address
  savedAddresses: CustomerAddress[] = [];
  selectedShippingAddressId: number | null = null;
  selectedBillingAddressId: number | null = null;
  showAddressModal = false;
  addressEditData: any = null;
  showBillingAddressModal = false;
  billingAddressEditData: any = null;
  addressLoading = false;
  addressError = '';

  // Shipping
  shippingMethods: ShippingMethod[] = [];
  selectedShippingId: number | null = null;
  shippingLoading = false;

  // Pricing — Total Amount and Total Discount are tax-EXCLUSIVE bases.
  // GST is shown on its own line below the discount.
  totalAmount = 0;        // sum of pd.total (base MRP, before GST)
  totalDiscount = 0;      // sum of pd.discount (base discount, before GST)
  totalGst = 0;           // sum of pd.tax (GST portion)
  // Server-quoted, never computed here. `shippingAmount` is the EX-TAX portion
  // and `shippingTax` the GST inside it; together they are what the customer pays.
  shippingAmount = 0;
  shippingBaseAmount = 0;  // the selected option's total (what the customer pays)
  isShippingFree = false;
  shippingTax = 0;

  /**
   * The cart and pincode the current delivery quote was made for.
   *
   * Kept so a CART change can trigger a fresh quote. Delivery depends on the
   * cart, not just the address — free standard delivery applies over a threshold
   * (₹499 today) and always on wallpaper — so a cart edit can move delivery
   * between free and charged.
   *
   * Deliberately a cart SIGNATURE rather than a total compared against the
   * threshold. The page does not know the threshold and must not: it is a
   * `settings` row the server owns, and duplicating it here is exactly the
   * duplication that had the page showing ₹177 while the server charged ₹168.
   * So the page reports "the cart changed" and the server re-decides.
   *
   * It also makes the refetch loop-free: fetchShipping() records the signature it
   * asked about, so the calculateTotals() that follows its response finds them
   * equal and stops.
   */
  private shippingQuotedFor: { cart: string; pincode: string } | null = null;

  /**
   * The server's checkout quote (GET /api/site/checkout/quote) for the rows being
   * bought — installation pooled and taxed exactly as the order will. `sig` says
   * which state of the cart it priced; `checkoutQuoteFor` what was last ASKED.
   */
  checkoutQuote: { sig: string; installBase: number; installTax: number } | null = null;
  private checkoutQuoteFor: string | null = null;
  checkoutQuoteLoading = false;

  /** What the checkout quote depends on: the rows bought and their services. */
  private checkoutQuoteSignature(): string {
    return this.cartItems
      .map((i: any) => `${i.id}:${i.quantity || 1}:${Number(i.site_install_charge) || 0}:${i.site_pincode || ''}`)
      .sort()
      .join('|');
  }

  private fetchCheckoutQuote(sig: string): void {
    this.checkoutQuoteFor = sig;
    if (!this.cartItems.length) { this.checkoutQuote = null; return; }
    const scope = this.buyNowMode && this.buyNowItemIds.length ? this.buyNowItemIds : null;
    this.checkoutQuoteLoading = true;
    this.orderService.getSiteCheckoutQuote(scope).subscribe({
      next: (res: any) => {
        const inst = res?.data?.installation || {};
        // A response for a cart that has since changed is dropped — the newer
        // request's answer is the one that counts.
        if (this.checkoutQuoteFor === sig) {
          this.checkoutQuote = { sig, installBase: Number(inst.base) || 0, installTax: Number(inst.tax) || 0 };
          this.checkoutQuoteLoading = false;
          this.calculateTotals();
        }
      },
      error: () => {
        // Keep the local figure; the order-time reconcile still adopts the server's.
        if (this.checkoutQuoteFor === sig) this.checkoutQuoteLoading = false;
      },
    });
  }

  /**
   * Guards the modal's outcome handlers against each other.
   *
   * Razorpay can fire more than one of handler / payment.failed / ondismiss for a
   * single attempt — a failure leaves the modal open, and closing it then fires
   * ondismiss on top of the failure we already handled. Set the moment any
   * terminal outcome is taken, so the later events no-op instead of double-
   * reporting or overwriting a navigation already under way.
   */
  private paymentResolved = false;

  amountPayable = 0;
  cartSubtotal = 0;   // GST-inclusive cart total before promo & shipping
  installationAmount = 0;   // ex-GST installation base, pooled per site by the server
  installationTax = 0;      // its service GST, added ON TOP once on that base

  // Promo
  promoCode = '';
  promoApplied = false;
  promoDiscount = 0;
  appliedPromo: any = null;
  showPromoDialog = false;

  // Billing
  needsGstInvoice = false;
  gstForm: { gstin: string; company_name: string; business_address: string } = {
    gstin: '', company_name: '', business_address: ''
  };

  // Installation per item
  installationExpanded: Set<string> = new Set();
  itemInstallation: Record<string, boolean> = {};

  /**
   * What the pincode lookup answered for a line, before any charge is added.
   *
   * Lets the card show "₹21/sq.ft in Ghaziabad" at the moment the customer decides,
   * instead of only after the charge lands. Cleared with the rest of the per-item
   * installation state.
   */
  itemInstallQuote: Record<string, { rate: number; city: string | null; fixed?: number | null; uptoSqft?: number | null }> = {};
  itemPincode: Record<string, string> = {};
  itemPincodeStatus: Record<string, string> = {};
  showInstallationAgreement = false;
  pendingInstallationItemId = '';

  // Payment
  orderLoading = false;
  paymentError = '';

  /*
   * Razorpay, replacing PayU.
   *
   * What used to live here was a `payuKey`, a `payuSalt` and a hidden form. The
   * salt is what tells PayU an amount has not been tampered with, and it was
   * shipped in this bundle and used HERE to build the SHA-512 hash — so the party
   * that could change the amount was also the party signing it. Anyone with
   * DevTools could sign ₹1 for a ₹28,296 order and PayU would take the hash as
   * genuine.
   *
   * Nothing secret remains on this page. The only credential Razorpay needs in
   * the browser is its publishable key, and even that is not read from the
   * environment — it arrives from `razorpayStart()` together with an order id the
   * server created from OUR order row. The browser cannot state an amount at all.
   *
   * There is deliberately no field holding that response either. It is passed
   * straight from `initiateRazorpay()` into `openRazorpayCheckout()` and then to
   * the SDK, so a stale order id from an earlier attempt cannot be picked up by a
   * later one.
   */

  // Detail dialog
  showDetailDialog = false;
  detailDialogItem: any = null;

  // Open detail dialog — passes item as-is, dialog reads pricing_data directly
  openDetailDialog(item: any) {
    this.detailDialogItem = item;
    this.showDetailDialog = true;
  }
  detailExpanded: Set<string> = new Set();

  // Guest checkout
  guestStep = 1;
  guestShippingForm: any = { fullname: '', email: '', mobile: '', address: '', city: '', city_id: 0, zipcode: '', company_name: '', gst: '', pan: '' };
  guestBillingForm: any = { fullname: '', mobile: '', address: '', city: '', city_id: 0, zipcode: '' };
  guestFormError = '';
  billingSameAsShipping = true;
  guestShippingAddressId: number | null = null;
  guestBillingAddressId: number | null = null;

  // Guest city autocomplete
  guestCities: any[] = [];
  guestCityLoading = false;
  guestCityText = '';
  showGuestCityDropdown = false;
  guestSelectedCity: any = null;

  private beginCheckoutFired = false;

  constructor(
    public router: Router,
    public state: AppStateService,
    public auth: AuthService,
    private orderService: OrderService,
    private wallpaperService: WallpaperService,
    private dataLayer: DataLayerService,
    private route: ActivatedRoute,
  ) {}

  // ── Buy Now mode ────────────────────────────────────────────────
  // When true, this checkout targets ONLY the product(s) the user clicked "Buy Now" on
  // (identified by buyNowItemIds = Cart row ids), not the whole cart. Default false keeps
  // the normal Add-to-Cart checkout behaviour completely unchanged.
  buyNowMode = false;
  buyNowItemIds: number[] = [];

  cartLoading = true;

  /**
   * True while a quantity change and the reload it triggers are in flight. Mirrors
   * the shared `AppStateService.cartBusy$`. While on, a blocking overlay covers the
   * checkout and the +/- / Remove controls are disabled, so a second overlapping
   * update — whose out-of-order response could leave the Order Summary stale —
   * cannot start. A single global flag because the reload refetches the whole cart.
   */
  cartBusy = false;

  ngOnInit() {
    // Buy Now mode — set from route data {buyNow:true} + ?items=<cartId,cartId> query param.
    // Read synchronously up-front so the cart subscription below filters correctly.
    this.buyNowMode = this.route.snapshot.data['buyNow'] === true;
    if (this.buyNowMode) {
      const raw = this.route.snapshot.queryParamMap.get('items') || '';
      this.buyNowItemIds = raw.split(',').map(s => Number(s.trim())).filter(n => n > 0);
    }

    // Subscribe to raw cart data from AppStateService
    this.sub.add(this.state.cartRaw$.subscribe(rawData => {
      if (rawData.length > 0) {
        this.cartItems = rawData.map((entry: any) => this.mapCartItem(entry)).filter((i: any) => i);
        // Buy Now: keep only the clicked product's cart row(s); leave the rest of the cart alone.
        if (this.buyNowMode) {
          this.cartItems = this.cartItems.filter((i: any) => this.buyNowItemIds.includes(Number(i.id)));
        }
      } else if (this.state.cartLoaded$.value) {
        this.cartItems = [];
      }
      // Seed installation checkbox from backend for items without an explicit user choice
      for (const it of this.cartItems) {
        const id = String(it.id);
        if (this.itemInstallation[id] === undefined && (it.pricing_data?.installation_amount || 0) > 0) {
          this.itemInstallation[id] = true;
        }
      }
      if (this.state.cartLoaded$.value) {
        this.cartLoading = false;
        this.calculateTotals();
        // E1 — begin_checkout (now fired server-side from backend generateOrder)
        // if (!this.beginCheckoutFired && this.cartItems.length > 0) {
        //   this.beginCheckoutFired = true;
        //   this.dataLayer.pushBeginCheckout(this.cartItems.map((item: any) => ({
        //     item_id: item.sku || String(item.id),
        //     item_name: item.name || '',
        //     price: item.price || 0,
        //     quantity: item.quantity || 1
        //   })));
        // }
      }
    }));

    // When cart finishes loading (even if empty), stop spinner
    this.sub.add(this.state.cartLoaded$.subscribe(loaded => {
      if (loaded) {
        this.cartLoading = false;
        this.calculateTotals();
      }
    }));

    // Quantity-update overlay: on while a +/- change is being applied + reloaded.
    this.sub.add(this.state.cartBusy$.subscribe(b => this.cartBusy = b));

    // Always pull a FRESH cart and hold the loader until it lands.
    //
    // Without forcing, a stale cartLoaded$ — left true by earlier browsing, or by a
    // Buy Now reload still in flight when we navigated here — let the page fall
    // through to the "cart is empty" state for a frame before the real cart arrived.
    // Resetting cartLoaded$ keeps the spinner up until the fresh data is applied.
    this.cartLoading = true;
    this.state.cartLoaded$.next(false);
    this.state.loadCartFromApi(true);

    if (this.auth.isLoggedIn) {
      this.loadAddresses();
    }

    // When user logs in while on checkout → load their addresses and advance to shipping step
    let prevLoggedIn = this.auth.isLoggedIn;
    this.sub.add(this.auth.user$.subscribe(user => {
      const nowLoggedIn = !!user;
      if (nowLoggedIn && !prevLoggedIn) {
        this.loadAddresses();
        this.currentStep = 2;
        const customerId = Number(user?.customers_id) || Number(user?.id) || 0;
        this.orderService.beginCheckout({
          customer_id: customerId,
          value: this.amountPayable,
          coupon: this.appliedPromo?.code || '',
        }).subscribe();
        if (!this.beginCheckoutFired && this.cartItems.length > 0) {
          this.beginCheckoutFired = true;
          this.dataLayer.pushBeginCheckout(this.cartItems.map((item: any) => ({
            item_id: item.sku || item.wallpaper_id || String(item.id),
            item_name: item.name || '',
            item_category: item.category_name || 'Wallpaper',
            item_brand: 'MahattaArt',
            item_variant: item.color_name || item.size || '',
            price: item.calculated_total || item.pricing_data?.amount_payable || 0,
            quantity: item.quantity || 1
          })));
        }
      }
      prevLoggedIn = nowLoggedIn;
    }));

    /*
     * The abandoned-payment notice used to live here.
     *
     * It read a `payu_payment_pending` flag out of sessionStorage, because PayU
     * took the customer away on a full-page POST and the only way to know they had
     * come back without paying was a breadcrumb left before leaving.
     *
     * Razorpay opens in a modal on this page, so there is no navigation and no
     * breadcrumb to read: dismissing it lands in `modal.ondismiss`, which sets the
     * same message directly and immediately. Removed rather than left behind,
     * because nothing writes that flag any more and a reader with no writer only
     * looks like a feature.
     */
  }

  loadCartFromApi() {
    this.state.loadCartFromApi(true);
  }

  // Navigate to the item's detail page using the same routes the listing /
  // wall-art / collage-set / featured-frames pages use:
  //   • Catalog wall art (on_sale_id > 0, not frame) → /featured-art/:slug
  //   • Catalog featured frame (on_sale_id > 0, frame) → /featured-frame/:slug
  //   • Collage set (custom)                          → /collage-set/:slug
  //   • Custom photo frame / print / canvas (no on_sale row) → print-frame landing
  //   • Wallpaper                                      → buildPdpUrl (PDP)
  goToPdp(item: any) {
    const onSaleId = item.on_sale_id || 0;
    const onSaleSeg = item.on_sale_title_slug
      || item.on_sale?.title_slug
      || item.on_sale?.slug
      || item.on_sale?.caption_slug
      || item.on_sale?.caption
      || onSaleId;

    if (onSaleId > 0 && this.isWallArtItem(item) && onSaleSeg) {
      this.router.navigate(['/featured-art', onSaleSeg]);
      return;
    }
    if (onSaleId > 0 && this.isFrameOnSaleItem(item) && onSaleSeg) {
      this.router.navigate(['/featured-frame', onSaleSeg]);
      return;
    }

    const isCollage = (item.collage_layout_id || 0) > 0 || (item.url || '').includes('collage-set');
    if (isCollage) {
      const cl = item.collage_layout || {};
      let cachedSlug = '';
      try {
        const stored = JSON.parse(localStorage.getItem('collage_prices') || '{}');
        cachedSlug = stored[item.collage_layout_id]?.slug || '';
      } catch {}
      const seg = item.collage_slug
        || cl.slug
        || cl.caption_slug
        || cl.title_slug
        || cl.caption
        || cachedSlug
        || item.collage_layout_id
        || item.id;
      if (seg) { this.router.navigate(['/collage-set', seg]); return; }
    }

    // Custom print/frame uploads (no on_sale row): reopen the same product
    // customization URL it was created from (carries product, print_surface,
    // image_size, frame_code, glass_type, etc. as query params).
    if (this.isFrameOnSaleItem(item) || this.isPrintFrameItem(item)) {
      const savedUrl = (item.url || '').toString();
      if (savedUrl) {
        const path = savedUrl.startsWith('/') ? savedUrl
                   : savedUrl.startsWith('product-customization') ? `/${savedUrl}`
                   : savedUrl.startsWith('product=') ? `/product-customization?${savedUrl}`
                   : '';
        if (path) { this.router.navigateByUrl(path); return; }
      }
      // Fall back to the matching print-frame landing.
      const type = (item.size_type_label || item.wallpaper_type || '').toString().toLowerCase();
      let landing = 'photo-frame';
      if (type.includes('only') && type.includes('print')) landing = 'only-print';
      else if (type.includes('frame') && type.includes('only')) landing = 'frame-only';
      else if (type.includes('canvas')) landing = 'canvas';
      this.router.navigate(['/print-frame', landing]);
      return;
    }

    // Wallpaper fallback
    const url = buildPdpUrl({
      id: item.wallpaper_internal_id || item.id,
      slug: item.slug,
      collection_slug: item.collection_slug,
      max_color: item.max_color,
      min_color: item.min_color,
    } as any);
    this.router.navigateByUrl(url);
  }

  private mapCartItem(cartEntry: any): any {
    if (!cartEntry) return null;
    const cp = cartEntry.customizedProduct || cartEntry;
    if (!cp) return null;

    // pricing_detail may be JSON string from DB
    let pd = cp.pricing_detail || {};
    if (typeof pd === 'string') { try { pd = JSON.parse(pd); } catch { pd = {}; } }

    const items = cp.customizedProductItems || cp.customized_product_items || [];
    const firstItem = items[0] || {};

    // image_detail may be JSON string from DB
    let imgDetail = firstItem.image_detail || {};
    if (typeof imgDetail === 'string') { try { imgDetail = JSON.parse(imgDetail); } catch { imgDetail = {}; } }

    // Check if this is an on-sale item (featured frame/art)
    const onSale = (cp.on_sale_id > 0 && cp.on_sale) ? cp.on_sale : null;

    // Determine if this is a collage set (product_id=2) or wallpaper (product_id=1)
    const isCollageSet = !onSale && (cp.collage_layout_id > 0 || (cp.url || '').includes('collage-set'));
    const collageLayoutObj = cp.collage_layout_detail || cp.collage_layout || cp.collageLayout || null;

    let calculatedTotal = 0;
    let totalWithTax = 0;
    let discountWithTax = 0;
    let discountRate = 0;
    let itemName = '';
    let itemImage = '';
    let itemSize = '';
    let itemType = '';

    if (onSale) {
      // Prefer backend on_sale fields, but derive MRP when list_price_tax is
      // missing so print/frame product cards don't show ₹0 strikethrough.
      calculatedTotal = Number(onSale.amount_payable) || 0;
      totalWithTax = Number(onSale.list_price_tax) || 0;
      discountWithTax = Number(onSale.discount_with_tax) || 0;
      discountRate = Number(onSale.discount_rate) || Number(onSale.discount) || 0;
      if (!totalWithTax) {
        const listPrice = Number(onSale.list_price) || 0;
        const taxRate = Number(onSale.tax_rate) || 0;
        if (listPrice && taxRate) {
          totalWithTax = Number((listPrice * (1 + taxRate / 100)).toFixed(2));
        } else if (calculatedTotal && discountRate > 0 && discountRate < 100) {
          // amount_payable = list_price_tax * (1 - rate/100)  →  invert
          totalWithTax = Number((calculatedTotal / (1 - discountRate / 100)).toFixed(2));
        } else if (calculatedTotal) {
          totalWithTax = Number((calculatedTotal + discountWithTax).toFixed(2));
        }
      }
      // Same fix for discount: derive when backend left discount_with_tax blank.
      if (!discountWithTax && totalWithTax && calculatedTotal) {
        discountWithTax = Number((totalWithTax - calculatedTotal).toFixed(2));
      }
      // GST portion baked into amount_payable. Default 18% when tax_rate missing.
      const onSaleTaxRate = Number(onSale.tax_rate) || 18;
      let onSaleTax = Number(onSale.tax) || Number(onSale.total_tax) || 0;
      if (!onSaleTax && calculatedTotal) {
        onSaleTax = Number((calculatedTotal * onSaleTaxRate / (100 + onSaleTaxRate)).toFixed(2));
      }
      const onSaleNetPrice = Number(onSale.net_price) > 0
        ? Number(onSale.net_price)
        : Number((calculatedTotal - onSaleTax).toFixed(2));
      itemName = onSale.title || onSale.caption || cp.name || 'Featured Frame';
      itemImage = onSale.list_thumbnail || onSale.hover_thumbnail || onSale.image_1 || imgDetail.image_path || cp.image || '';
      itemSize = onSale.final_size || firstItem.final_size || firstItem.image_size || '';
      itemType = 'FEATURED';

      pd = {
        total: Number(onSale.list_price) || 0,
        total_with_tax: totalWithTax,
        discount: Number(onSale.discount_amount) || 0,
        discount_with_tax: discountWithTax,
        discount_rate: discountRate,
        amount_taxable: onSaleNetPrice,
        tax: onSaleTax,
        tax_rate: onSaleTaxRate,
        amount_payable: calculatedTotal,
        gift_wrap: Number(onSale.gift_wrap) || 0,
        gift_tax: Number(onSale.gift_tax) || 0,
        installation_amount: Number(onSale.installation_amount) || 0,
        installation_tax: Number(onSale.installation_tax) || 0,
      };
    } else {
      // CUSTOM PRODUCT / COLLAGE: prices from pricing_detail
      calculatedTotal = pd.amount_payable || 0;
      totalWithTax = pd.total_with_tax || 0;
      discountWithTax = pd.discount_with_tax || 0;
      discountRate = (pd.discount_rate != null) ? pd.discount_rate : 15;
      const isConsultation = cp.url && cp.url.includes('product=consultation');
      itemName = isCollageSet
        ? (collageLayoutObj?.caption || imgDetail.image_caption || 'Collage Set')
        : isConsultation
          ? 'Wall Measurement by an Expert'
          : (imgDetail.image_caption || cp.name || cp.product_type?.name || cp.product?.name || 'Product');
      itemImage = isCollageSet
        ? (collageLayoutObj?.list_thumbnail || imgDetail.image_path || cp.image || '')
        : (imgDetail.image_path || cp.image || '');
      itemSize = isCollageSet
        ? (pd.finished_size || collageLayoutObj?.final_size || firstItem.final_size || firstItem.image_size || '')
        : (firstItem.image_size || firstItem.final_size || '');
      itemType = isCollageSet ? 'COLLAGE SET' : isConsultation ? 'Wall Measurement by an Expert' : (cp.product_type?.name || 'Wallpaper');

      // Collage sets: if pricing_detail is missing, use collageLayout.final_price with 15% discount
      // This is the listing price — same price shown on listing, PDP, and checkout
      if (isCollageSet && calculatedTotal === 0) {
        const layoutFinalPrice = collageLayoutObj?.final_price || 0;
        if (layoutFinalPrice > 0) {
          totalWithTax = Number(layoutFinalPrice);
          discountRate = 15;
          discountWithTax = Number((totalWithTax * 0.15).toFixed(2));
          calculatedTotal = Number((totalWithTax - discountWithTax).toFixed(2));
        } else if (calculatedTotal === 0) {
          // Last resort: sum from frame prices
          let itemTotal = items.reduce((sum: number, item: any) => sum + (item.price || 0), 0);
          if (itemTotal === 0) {
            try {
              const fd = typeof cp.frame_detail === 'string' ? JSON.parse(cp.frame_detail) : (cp.frame_detail || []);
              itemTotal = fd.reduce((sum: number, f: any) => sum + (f.priceDetail?.amount_payable || f.price || 0), 0);
            } catch {}
          }
          if (itemTotal > 0) {
            calculatedTotal = itemTotal;
            totalWithTax = Math.round(itemTotal * 1.18);
          }
        }
      }
    }

    // Sample kit: the price is the SERVER's — the new backend's cart read priced
    // the kit (withSiteWallpaperPricing put it in `site_line_total` and
    // `pricing_detail.amount_payable`). This used to recompute `199 + (n − 1) × 50`
    // here, a fourth copy of the rule that would drift the moment it changed.
    let surfaceName = firstItem.paper?.display_name || firstItem.paper?.name || '';
    const isSample = !onSale && (pd?.is_sample || !!cp.site_is_sample || (cp.url && cp.url.includes('sampleType=surface-kit')));
    if (isSample) {
      const serverTotal = Number(cp.site_line_total) || Number(pd?.amount_payable) || 0;
      if (serverTotal > 0) {
        calculatedTotal = serverTotal;
        totalWithTax = serverTotal;
      }
      // A kit is a fixed price — nothing comes off it.
      discountWithTax = 0;
      discountRate = 0;
      if (pd?.surfaces && Array.isArray(pd.surfaces) && pd.surfaces.length > 0) {
        surfaceName = pd.surfaces.join(', ');
      } else if (cp.url && cp.url.includes('surfaces=')) {
        try {
          const qs = (cp.url.split('?')[1] || '');
          const urlP = new URLSearchParams(qs);
          const surfaces = urlP.get('surfaces');
          if (surfaces) surfaceName = decodeURIComponent(surfaces);
        } catch {}
      }
    }

    const wp = cp.wallpaper || {};

    return {
      // IDs
      id: cartEntry.id || cp.id,
      cart_id: cartEntry.id,
      customized_product_id: cp.id,
      // Prefer the SKU string (e.g. "MA-0042") from the joined wallpaper row
      // or image_detail; cp.wallpaper_id alone is the numeric FK.
      wallpaper_id: wp.wallpaper_id || imgDetail.image_id || cp.wallpaper_id,
      product_id: cp.product_id,
      collage_layout_id: cp.collage_layout_id,
      on_sale_id: cp.on_sale_id || 0,
      wallpaper_type: itemType,

      // Routing — fields read by buildPdpUrl / goToPdp to construct the URL.
      slug: wp.slug || imgDetail.slug || '',
      collection_slug: wp.collection_slug || '',
      max_color: wp.max_color || '',
      min_color: wp.min_color || '',
      wallpaper_internal_id: wp.id || cp.wallpaper_id || 0,
      collage_slug: collageLayoutObj?.slug || collageLayoutObj?.caption_slug || collageLayoutObj?.title_slug || collageLayoutObj?.caption || '',
      collage_layout: collageLayoutObj || null,
      on_sale_title_slug: onSale?.title_slug || onSale?.slug || onSale?.caption_slug || onSale?.caption || '',
      // Original customization URL (preserves the product=frame_your_photos&print_surface=...&frame_code=... query)
      url: cp.url || '',

      // Display
      name: itemName,
      image: itemImage,
      sku: imgDetail.image_id || cp.wallpaper_id || '',
      surface_name: surfaceName,
      is_sample: !!isSample,
      glass_name: isCollageSet ? (firstItem.glass_type?.name || firstItem.glass?.name || firstItem.glass_name || '') : '',
      roll_size: cp.roll_size || firstItem.roll_size || itemSize,
      size_label: itemSize,
      size_type_label: cp.product_type?.name || '',
      product_type: cp.product_type || null,

      // Pricing
      quantity: cp.quantity || 1,
      price: pd.printRate || 0,
      calculated_total: calculatedTotal,
      // Per-sq-ft figures, and the area they divide by.
      //
      // `cp.wallpaperSizeArr` was the only source of area, and the old backend never
      // stored that column — the PDP sent it and addToCart dropped it — so on a row
      // written by this backend the condition is false and the FALLBACK ran. That
      // fallback did `printRate + printRate * 0.18`, and `printRate` is now the
      // tax-INCLUSIVE rate the PDP quoted, so it taxed an already-taxed figure with a
      // literal 18 that is not even the surface's slab.
      //
      // `site_area_sqft` comes off the line's own stored quote, so the area is known
      // without parsing anything, and the inclusive rate is used as-is.
      per_sqft_price: !onSale && pd.amount_payable && this.lineArea(cp, items) > 0
        ? pd.amount_payable / this.lineArea(cp, items)
        : pd.printRate || 0,
      per_sqft_mrp: !onSale && totalWithTax && this.lineArea(cp, items) > 0
        ? totalWithTax / this.lineArea(cp, items)
        : pd.printRate || 0,
      total_area: this.lineArea(cp, items),
      discount_rate: discountRate,
      no_of_rolls: cp.quantity || 1,

      // Collage listing price (for consistent pricing across listing/PDP/checkout)
      collage_final_price: isCollageSet ? this.getCollageListingPrice(collageLayoutObj, cp.collage_layout_id) : 0,

      // Raw data
      pricing_data: { ...pd, amount_payable: calculatedTotal || pd.amount_payable, total_with_tax: totalWithTax || pd.total_with_tax },
      paper_id: firstItem.paper_id,
      customizedProductItems: items,
      on_sale: onSale,

      // Site-service facts, straight from the new backend's cart read. Carried onto
      // the item so the card and the summary can state what this line actually
      // holds — which service, at which ₹/sq ft, in which city — instead of
      // inferring it from a pincode or from local component flags.
      site_has_installation: !!cp.site_has_installation,
      site_has_expert_visit: !!cp.site_has_expert_visit,
      site_install_charge: Number(cp.site_install_charge) || 0,
      site_install_rate: Number(cp.site_install_rate) || 0,
      // Fixed small-order installation charge (decided by the server's CartReader).
      site_install_fixed: !!cp.site_install_fixed,
      site_visit_fee: Number(cp.site_visit_fee) || 0,
      site_service_city: cp.site_service_city || null,
      site_pincode: cp.site_pincode || null,
      site_billable_sqft: Number(cp.site_billable_sqft) || 0,
      site_rate_ex_gst: Number(cp.site_rate_ex_gst) || 0,
      site_rate_incl: Number(cp.site_rate_incl) || 0,
      site_base: Number(cp.site_base) || 0,
      site_gst_per: Number(cp.site_gst_per) || 0,

      // For API calls
      customized_product_id_raw: cp.id,
    };
  }

  /**
   * Sq ft this cart line covers, whole line (walls × quantity).
   *
   * Three sources, best first:
   *   1. `site_area_sqft` — the area the server stored WITH the quote, so no parsing.
   *   2. `wallpaperSizeArr` — the JSON the old PDP sent. Present only on rows old
   *      enough to predate this backend, because `addToCart` never saved that column
   *      in the first place; kept because a legacy row may still carry it in memory.
   *   3. The item's own size string, via getAreaFromItems.
   *
   * Having one place answer this is the point: `total_area` feeds the installation
   * note and both per-sq-ft figures, and they were each falling back differently.
   */
  private lineArea(cp: any, items: any[]): number {
    const stored = Number(cp?.site_area_sqft);
    if (Number.isFinite(stored) && stored > 0) return stored;
    return this.getAreaFromSizeArr(cp?.wallpaperSizeArr)
      || Number(cp?.pricing_detail?.total_area)
      || this.getAreaFromItems(items);
  }

  private getAreaFromSizeArr(wallpaperSizeArr: any): number {
    try {
      const arr = typeof wallpaperSizeArr === 'string' ? JSON.parse(wallpaperSizeArr) : wallpaperSizeArr;
      if (!Array.isArray(arr) || arr.length === 0) return 0;
      // Match PDP totalWallArea: sum of width × height per wall, rounded to 2 dp.
      // Prefer customWidth/customHeight when width/height aren't populated.
      const total = arr.reduce((sum: number, w: any) => {
        const width = Number(w.width) || Number(w.customWidth) || 0;
        const height = Number(w.height) || Number(w.customHeight) || 0;
        return sum + width * height;
      }, 0);
      return parseFloat(total.toFixed(2));
    } catch {
      return 0;
    }
  }

  // Fallback: derive area by parsing each item's "WxH" image_size string.
  // Used when wallpaperSizeArr is missing (older cart entries).
  private getAreaFromItems(items: any[]): number {
    if (!Array.isArray(items) || items.length === 0) return 0;
    const total = items.reduce((sum: number, it: any) => {
      const size = String(it?.image_size || it?.final_size || '');
      const m = size.match(/([\d.]+)\s*[xX]\s*([\d.]+)/);
      if (!m) return sum;
      return sum + (parseFloat(m[1]) || 0) * (parseFloat(m[2]) || 0);
    }, 0);
    return parseFloat(total.toFixed(2));
  }

  ngOnDestroy() { this.sub.unsubscribe(); }

  // Step navigation
  goToStep(step: number) {
    if (step === 2 && this.cartItems.length === 0) return;
    // Login check: must be logged in to proceed to shipping
    if (step === 2 && !this.auth.isLoggedIn) {
      this.auth.requestLogin();
      return;
    }
    if (step === 2) {
      const user = this.auth.currentUser;
      const customerId = Number(user?.customers_id) || Number(user?.id) || 0;
      this.orderService.beginCheckout({
        customer_id: customerId,
        value: this.amountPayable,
        coupon: this.appliedPromo?.code || '',
      }).subscribe();
      if (!this.beginCheckoutFired && this.cartItems.length > 0) {
        this.beginCheckoutFired = true;
        this.dataLayer.pushBeginCheckout(this.cartItems.map((item: any) => ({
          item_id: item.sku || item.wallpaper_id || String(item.id),
          item_name: item.name || '',
          item_category: item.category_name || 'Wallpaper',
          item_brand: 'MahattaArt',
          price: item.calculated_total || item.pricing_data?.amount_payable || 0,
          quantity: item.quantity || 1
        })));
      }
    }
    this.currentStep = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  get selectedShippingAddress(): CustomerAddress | null {
    if (this.selectedShippingAddressId) {
      return this.savedAddresses.find(a => a.id === this.selectedShippingAddressId) || null;
    }
    return null;
  }

  get selectedBillingAddress(): CustomerAddress | null {
    if (this.selectedBillingAddressId) {
      return this.savedAddresses.find(a => a.id === this.selectedBillingAddressId) || null;
    }
    return this.selectedShippingAddress;
  }

  // Pricing — uses listing price for collage sets, pricing_detail for others
  calculateTotals() {
    // Helper: derive base (without GST) from with-GST + tax_rate.
    const baseFromWithTax = (withTax: number, taxRate: number): number => {
      const factor = taxRate ? 1 + taxRate / 100 : 1;
      return Number((withTax / factor).toFixed(2));
    };

    // Total Amount = sum of pd.total (base MRP, no GST). Fallback: back-calc from
    // pd.total_with_tax / pd.tax_rate when pd.total isn't populated.
    this.totalAmount = this.cartItems.reduce((sum, item) => {
      const cfp = this.getCollageFinalPrice(item);
      if (cfp > 0) return sum + (baseFromWithTax(cfp, 18) * (item.quantity || 1));
      const pd = item.pricing_data || {};
      const base = pd.total || baseFromWithTax(pd.total_with_tax || item.calculated_total || 0, this.lineTaxRate(item));
      return sum + (base * (item.quantity || 1));
    }, 0);

    // Total Discount = sum of pd.discount (base discount, no GST).
    // Per line from lineDiscountExGst(), so the summary's product cards add up to it.
    this.totalDiscount = this.cartItems.reduce((sum, item) => sum + this.lineDiscountExGst(item), 0);

    // GST = sum of pd.tax (the tax portion of each item).
    this.totalGst = this.cartItems.reduce((sum, item) => {
      const pd = item.pricing_data || {};
      // pd.tax (or pd.total_tax) is the GST already inside the sale price.
      let tax = Number(pd.tax) || Number(pd.total_tax) || 0;
      // Fallback when neither is populated: payable − base-after-discount.
      if (!tax) {
        const taxRate = this.lineTaxRate(item);
        const baseAfterDiscount = baseFromWithTax((pd.amount_payable || item.calculated_total || 0), taxRate);
        const payable = Number(pd.amount_payable) || Number(item.calculated_total) || 0;
        tax = Number((payable - baseAfterDiscount).toFixed(2));
      }
      return sum + (tax * (item.quantity || 1));
    }, 0);

      // Installation, entirely from the server.
      //
      // The `area × 20 × quantity` fallback that used to sit here is gone. ₹20 was a
      // client-side guess at a rate Production sets PER CITY — pincode 201003 resolves
      // ₹21, Ghaziabad's own row says ₹13 — so it was wrong everywhere except by
      // coincidence. It only existed because ticking the box here never wrote anything
      // to the cart row; it does now (apiUpdateCartServices), so every rupee below is
      // one the server resolved and there is nothing left to guess.
      //
      // Its service GST is added ONCE on this base just below (the server's own rule),
      // and the server's checkout quote then replaces both with the order's figures.
      this.installationAmount = this.cartItems.reduce((sum, item) => {
        // A removal the customer has just made but the server has not confirmed is
        // excluded straight away. Without this the checkbox unticked instantly while
        // the summary went on charging ₹1,680 until the request came back, so the two
        // halves of the same screen disagreed for as long as the round trip took.
        //
        // Note what this does NOT do: invent an amount for a pending ADD. The charge
        // for a new installation is only known once the server has resolved the
        // pincode, so that direction waits for the answer and shows a spinner instead
        // of guessing a figure.
        if (this.itemInstallation[String(item.id)] === false) return sum;
        const pd = item.pricing_data || {};
        const qty = item.quantity || 1;
        return sum + ((pd.installation_amount || 0) + (pd.installation_tax || 0)) * qty;
      }, 0);
      // Installation's service GST, ONCE on the order's installation base — the
      // same Money::installation the server applies, so this is what Razorpay charges.
      const instLocal = exclusiveMoney(this.installationAmount, this.serviceGstPer);
      this.installationAmount = instLocal.base;
      this.installationTax = instLocal.tax;

      // The SERVER's figure wins once it has priced exactly these rows: the order
      // pools installation on the rows BEING BOUGHT at today's city rate, which a sum
      // of cart rows cannot know (a Buy Now beside another wall in the cart showed
      // ₹614 for ₹1,180 billed). The local sum above only bridges a change in flight.
      const quoteSig = this.checkoutQuoteSignature();
      const pendingChange = this.cartItems.some(i =>
        this.itemInstallation[String(i.id)] === false && Number(i.site_install_charge) > 0);
      if (this.checkoutQuote && this.checkoutQuote.sig === quoteSig && !pendingChange) {
        this.installationAmount = this.checkoutQuote.installBase;
        this.installationTax = this.checkoutQuote.installTax;
      }
      if (!pendingChange && quoteSig !== this.checkoutQuoteFor) {
        this.fetchCheckoutQuote(quoteSig);
      }

    // Amount payable (with GST — final amount user pays)
    const cartPayable = this.cartItems.reduce((sum, item) => {
      const cfp = this.getCollageFinalPrice(item);
      if (cfp > 0) return sum + (Number((cfp * 0.85).toFixed(2)) * (item.quantity || 1));
      const pd = item.pricing_data || {};
      return sum + ((pd.amount_payable || item.calculated_total || 0) * (item.quantity || 1));
    }, 0);

    this.cartSubtotal = cartPayable;

    /*
     * Re-read the SELECTED option's figures. It does NOT re-derive them.
     *
     * `applyShippingOffer()` used to run here, re-testing the free-delivery rule
     * against the new cart total with this page's own ₹499 threshold and its own
     * tax rate — which is how the summary came to show ₹177 for a delivery the
     * server charged ₹168 for. Both decisions belong to the server now.
     */
    this.applySelectedShipping();

    /*
     * …and if the cart itself has changed, ask the server to quote again.
     *
     * This is the half that keeps the ₹499 threshold honest as the cart moves:
     * removing an item can drop the cart below it and reinstate the charge,
     * adding one can cross it and make delivery free. The page reports the change
     * and the server re-decides — it never tests the threshold itself.
     *
     * Deliberately AFTER applySelectedShipping(), so the totals below are always
     * computed from a coherent set of figures: the previous quote until the new
     * one lands, rather than a blank while a request is in flight.
     */
    this.refetchShippingIfCartChanged();

    // Σ components (each to the paisa); what Razorpay collects — the order's grand
    // total — to the rupee, the difference shown as Round off (9 Oct 2026). The
    // server stores exactly this; reconcileWithServer() adopts its figure anyway.
    const components = roundMoney(cartPayable - this.promoDiscount + this.shippingAmount + this.shippingTax + this.installationAmount + this.installationTax);
    this.amountPayable = roundTotal(components);
  }

  // Get collage listing price: from API response, or from localStorage (saved by PDP)
  private getCollageListingPrice(collageLayoutObj: any, collageLayoutId: number): number {
    // 1st: from API response
    if (collageLayoutObj?.final_price) return Number(collageLayoutObj.final_price);
    // 2nd: from localStorage (saved when adding to cart from PDP)
    if (collageLayoutId) {
      try {
        const stored = JSON.parse(localStorage.getItem('collage_prices') || '{}');
        if (stored[collageLayoutId]?.final_price) return stored[collageLayoutId].final_price;
      } catch {}
    }
    return 0;
  }

  // For collage sets, get the listing price (final_price) from mapped cart item
  private getCollageFinalPrice(item: any): number {
    return item.collage_final_price || 0;
  }

  getItemSalePrice(item: any): number {
    // Collage sets: always use listing price (final_price * 0.85)
    const cfp = this.getCollageFinalPrice(item);
    if (cfp > 0) return Number((cfp * 0.85).toFixed(2));

    const pd = item.pricing_data || {};
    if (pd.amount_payable) return pd.amount_payable;
    if (item.calculated_total) return item.calculated_total;
    // Wallpaper fallback when the backend sent no payable amount. No flat
    // discount is applied — wallpapers are sold at the actual rate.
    // Last resort, and the rate is the LINE's own slab, not a literal 18 —
    // surfaces sit on different ones. Unreachable for wallpapers now that
    // amount_payable always arrives from the server.
    return item.price * (1 + (Number(item.pricing_data?.tax_rate) || 0) / 100);
  }

  // Wallpapers carry no flat discount, so MRP equals the payable amount and
  // nothing is derived upward. Collage sets keep their own discount below,
  // and per-item discounts still come from the backend's discount_rate.
  private readonly FLAT_DISCOUNT_RATE = 0;

  getItemMrp(item: any): number {
    // Collage sets: MRP = listing price (final_price)
    const cfp = this.getCollageFinalPrice(item);
    if (cfp > 0) return cfp;

    const pd = item.pricing_data || {};
    // Use actual stored MRP when available (on_sale items have list_price_tax; custom products have total_with_tax from pricing_detail)
    if (pd.total_with_tax) return pd.total_with_tax;
    const payable = pd.amount_payable || item.calculated_total || 0;
    if (payable) return Number((payable / (1 - this.FLAT_DISCOUNT_RATE / 100)).toFixed(2));
    if (item.per_sqft_mrp && item.total_area) return item.per_sqft_mrp * item.total_area;
    // Last resort, and the rate is the LINE's own slab, not a literal 18 —
    // surfaces sit on different ones. Unreachable for wallpapers now that
    // amount_payable always arrives from the server.
    return item.price * (1 + (Number(item.pricing_data?.tax_rate) || 0) / 100);
  }

  getItemDiscount(_item: any): number {
    return this.FLAT_DISCOUNT_RATE;
  }

  get totalMrp(): number {
    return this.cartItems.reduce((sum, item) => sum + this.getItemMrp(item) * (item.quantity || 1), 0);
  }

  getItemTotal(item: any): number {
    // Collage sets: use listing price
    const cfp = this.getCollageFinalPrice(item);
    if (cfp > 0) return Number((cfp * 0.85 * (item.quantity || 1)).toFixed(2));

    const pd = item.pricing_data || {};
    if (pd.amount_payable) return pd.amount_payable * (item.quantity || 1);
    if (item.calculated_total) return item.calculated_total * (item.quantity || 1);
    return this.getItemSalePrice(item) * (item.quantity || 1);
  }

  // ── Order summary breakdown (for new price card) ──
  // Item-type predicates — single source of truth, used by counts AND groupedCartItems.
  // on_sale items belong to either Wall Art ('wall_art') or Print & Frame
  // ('frame_your_photos'). We can't always rely on on_sale.product being present
  // on the cart relation, so detection falls back to multiple frame-specific signals.
  isFrameOnSaleItem(i: any): boolean {
    const os = i.on_sale || {};
    const product = (os.product || os.product_slug || '').toString().toLowerCase();
    if (product === 'frame_your_photos') return true;
    if (product === 'wall_art') return false;
    // product_type slugs unique to frame_your_photos (print_only / frame_only)
    const ptSlug = (os.product_type?.slug || '').toString().toLowerCase();
    if (ptSlug === 'print_only' || ptSlug === 'frame_only') return true;
    // Fallback: derive from frame-specific signals on the cart entry / on_sale relation.
    const firstItem = (i.customizedProductItems && i.customizedProductItems[0]) || {};
    return (i.frame_id || 0) > 0
      || (firstItem.frame_id || 0) > 0
      || !!firstItem.frame
      || (os.frame_id || 0) > 0
      || !!os.frame_name
      || !!os.frame?.frame_name
      || (os.mount1_size || 0) > 0
      || (os.mount_id_1 || 0) > 0
      || !!os.mount_kit
      || !!os.glass_type;
  }
  private isWallArtItem(i: any): boolean {
    if (this.isFrameOnSaleItem(i)) return false;
    return !!i.on_sale || (i.on_sale_id || 0) > 0;
  }
  private isPrintFrameItem(i: any): boolean {
    if (this.isWallArtItem(i)) return false;
    if (this.isFrameOnSaleItem(i)) return true;
    return i.product_id === 2
      || (i.collage_layout_id || 0) > 0
      || /COLLAGE|FRAME|PRINT|CANVAS|PHOTO/i.test(i.wallpaper_type || '')
      || /COLLAGE|FRAME|PRINT|CANVAS|PHOTO/i.test(i.size_type_label || '');
  }
  isWallpaperItem(i: any): boolean {
    return !this.isWallArtItem(i) && !this.isPrintFrameItem(i);
  }
  isCollageSetItem(i: any): boolean {
    return ((i.collage_layout_id || 0) > 0)
      || (i.wallpaper_type || '').toUpperCase().includes('COLLAGE')
      || (i.url || '').includes('collage-set');
  }
  // Format size strings: "10.0000X10.0000" → "10×10", "10.5000X8.0000" → "10.5×8"
  formatSize(value: string): string {
    if (!value) return value;
    return value.split(/x/i).map(v => {
      const n = parseFloat(v);
      if (isNaN(n)) return v.trim();
      return n % 1 === 0 ? String(n) : parseFloat(n.toFixed(2)).toString();
    }).join('×');
  }

  // Public helper used by the template to label item-type pills.
  itemTypeLabel(i: any): string {
    if (this.isWallArtItem(i)) return 'WALL ART';
    if (this.isPrintFrameItem(i)) return 'PRINT & FRAME';
    return (i.wallpaper_type || 'WALLPAPER').toString().toUpperCase();
  }
  // Sub-product pills shown next to the main item-type badge.
  // e.g. WALL ART → ['FEATURED', 'CURATED', 'CANVAS ART']
  //      PRINT & FRAME → ['PHOTO FRAME']
  //      WALLPAPER → ['CONCEPT' | 'PATTERN']
  itemSubLabels(i: any): string[] {
    const labels: string[] = [];
    const seen = new Set<string>();
    const push = (val: any) => {
      const s = (val || '').toString().trim().toUpperCase();
      if (!s || seen.has(s)) return;
      seen.add(s);
      labels.push(s);
    };
    if (this.isWallArtItem(i)) {
      push('FEATURED');
      push('CURATED');
      push(i.on_sale?.product_type?.display_name || i.on_sale?.product_type?.name);
    } else if (this.isPrintFrameItem(i)) {
      if (this.isFrameOnSaleItem(i)) {
        // Featured frames / canvas / print-only — single sub-type pill only.
        const os = i.on_sale || {};
        const subType = os.product_type?.display_name
          || os.product_type?.name
          || os.product_type_name
          || os.type
          || i.wallpaper_type
          || (i.product_id === 2 || (i.collage_layout_id || 0) > 0 ? '' : 'Photo Frame');
        push(subType);
      } else {
        if (this.isCollageSetItem(i)) {
          push('Collage Set');
        } else {
          push(i.product_type?.name || i.size_type_label || '');
        }
      }
    } else {
      // Wallpapers: only surface (e.g. "Matte 175 GSM"). Skip wallpaper_type
      // since the main badge already says WALLPAPER.
      push(i.surface_name);
    }
    return labels;
  }

  get wallpaperCount(): number {
    return this.cartItems
      .filter(i => this.isWallpaperItem(i))
      .reduce((sum, i) => sum + (i.quantity || 1), 0);
  }

  get printFrameCount(): number {
    return this.cartItems
      .filter(i => this.isPrintFrameItem(i))
      .reduce((sum, i) => sum + (i.quantity || 1), 0);
  }

  get wallArtCount(): number {
    return this.cartItems
      .filter(i => this.isWallArtItem(i))
      .reduce((sum, i) => sum + (i.quantity || 1), 0);
  }

  get complimentaryCount(): number {
    return this.cartItems
      .filter(i => (i.pricing_data?.amount_payable || i.calculated_total || 0) === 0)
      .reduce((sum, i) => sum + (i.quantity || 1), 0);
  }

  /**
   * Whether the installation is the fixed small-order charge (all walls together
   * up to the configured sq ft) rather than area × rate — decided server-side
   * (CartReader), so the summary never prints a rate the order is not billed at.
   */
  get installationIsFixed(): boolean {
    return this.cartItems.some(i => this.hasInstallation(i) && !!i.site_install_fixed);
  }

  /** Combined wall area the installation is charged on. */
  get installationTotalSqft(): number {
    return this.cartItems
      .filter(i => this.hasInstallation(i) && (i.pricing_data?.installation_amount || 0) > 0)
      .reduce((s, i) => s + (Number(i.total_area) || 0), 0);
  }

  get installationItemCount(): number {
    // Counts only lines that will actually be billed for installation, so the
    // "(1 wall)" note cannot disagree with the amount beside it while a change is in
    // flight — `hasInstallation` already honours a pending removal.
    return this.cartItems.filter(i =>
      this.hasInstallation(i) && (i.pricing_data?.installation_amount || 0) > 0
    ).length;
  }

  /**
   * Lines with a service change in flight.
   *
   * Drives a small spinner on the row and disables its checkbox. Without it the
   * customer got no signal that anything was happening between the click and the
   * refetch — the tick moved, the summary did not, and the page looked broken rather
   * than busy.
   */
  installationBusy: Record<string, boolean> = {};

  isInstallationBusy(item: any): boolean {
    return !!this.installationBusy[String(item?.id)];
  }

  /**
   * Is the pincode form open for this line?
   *
   * A method rather than `installationExpanded.has(item.id)` in the template, and
   * that is the whole point: `installationExpanded` is a `Set<string>` while the
   * template hands it `item.id`, which is a NUMBER. A Set compares by identity, so
   * `has(502)` missed a stored `'502'` and the form never opened — the customer could
   * remove installation and then had no way to add it back, because the pincode step
   * was unreachable.
   *
   * Records elsewhere (`itemPincode`, `itemInstallation`) were unaffected: object keys
   * coerce a number to a string, a Set does not. Routing through here means the
   * template never has to know which it is.
   */
  isInstallationFormOpen(item: any): boolean {
    return this.installationExpanded.has(String(item?.id));
  }

  /**
   * The CART row id for a line, or 0 when it cannot be resolved.
   *
   * Guarded because an unresolved id used to reach the URL: the template passes
   * `item.id` as a NUMBER while these handlers type it as a string, so
   * `String(i.id) === itemId` compared '502' with 502, the lookup missed, and
   * `Number(undefined)` produced a request to /api/site/cart/NaN/services. The ids
   * are coerced at every entry point now; this returns 0 rather than NaN so a
   * caller can refuse instead of asking the server about nothing.
   */
  private cartIdOf(item: any): number {
    const id = Number(item?.cart_id ?? item?.id);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  /**
   * Whether THIS line is actually carrying an installation charge.
   *
   * Reads the row, not the component. The checkbox used to be bound to
   * `installationExpanded.has(id) || itemInstallation[id]`, so simply OPENING the
   * pincode form ticked it — the box claimed installation was added before a pincode
   * had even been checked — and after a refetch it still ignored what the row said.
   *
   * `itemInstallation[id] === true` is honoured on top so the tick does not flicker
   * off during the moment between the request and the refetch; an explicit `false`
   * (the customer just removed it) wins over a row not yet refetched.
   */
  hasInstallation(item: any): boolean {
    const id = String(item?.id);
    if (this.itemInstallation[id] === false) return false;
    if (this.itemInstallation[id] === true) return true;
    return !!item?.site_has_installation || (item?.pricing_data?.installation_amount || 0) > 0;
  }

  /**
   * Reopen the pincode form on a line that ALREADY has installation, so the city can
   * be changed without removing the charge first.
   *
   * The existing pincode is seeded in, because the customer is usually correcting a
   * digit rather than starting over. Nothing is written until they check the new
   * pincode and confirm — `setServices` then re-resolves the rate for the new city
   * and rewrites the line, so a move from Ghaziabad (₹21) to a ₹13 city re-prices
   * itself with no client arithmetic involved.
   */
  editInstallationPincode(itemId: string): void {
    itemId = String(itemId);
    const item = this.cartItems.find(i => String(i.id) === String(itemId));
    if (!this.itemPincode[itemId]) {
      this.itemPincode[itemId] = item?.site_pincode || item?.pricing_data?.pincode || '';
    }
    // Cleared so the form asks for a fresh check rather than reusing the verdict
    // that produced the charge already on screen.
    this.itemPincodeStatus[itemId] = '';
    delete this.itemInstallQuote[itemId];
    this.installationExpanded.add(itemId);
  }

  /** This line's installation charge — the server's figure, whole line. */
  installationChargeFor(item: any): number {
    const pd = item?.pricing_data || {};
    return ((pd.installation_amount || 0) + (pd.installation_tax || 0)) * (item?.quantity || 1);
  }

  /**
   * The ₹/sq ft this line's installation was quoted at, for the label.
   *
   * Derived from the charge and the area when the rate was not recorded (legacy
   * rows), because the two are the only things known there. Never a constant: the
   * rate is per city and Production edits it.
   */
  installationRateFor(item: any): number {
    const stored = Number(item?.site_install_rate);
    if (Number.isFinite(stored) && stored > 0) return stored;
    const area = Number(item?.total_area) || 0;
    const charge = this.installationChargeFor(item);
    return area > 0 && charge > 0 ? Math.round((charge / area) * 100) / 100 : 0;
  }

  /** The distinct cities the cart's installations are being done in. */
  get installationCities(): string[] {
    const cities = new Set<string>();
    this.cartItems.forEach(i => {
      if (this.hasInstallation(i) && i.site_service_city) cities.add(i.site_service_city);
    });
    return [...cities];
  }

  /**
   * Sq ft the installation covers, for the "(N walls)" note beside the charge.
   *
   * Read off the lines that actually carry an installation charge, rather than off a
   * local tri-state flag: the charge is now written to the row the moment it is
   * added, so the row IS the answer.
   */
  get installationArea(): number {
    return this.cartItems.reduce((sum, item) => {
      const pd = item.pricing_data || {};
      if ((pd.installation_amount || 0) <= 0) return sum;
      return sum + (Number(item.total_area) || 0) * (item.quantity || 1);
    }, 0);
  }

  get totalSavings(): number {
    // Derive from visible numbers (MRP − amount payable, both with tax) so
    // the displayed "Product discount" line balances against the MRP line.
    const productSavings = this.cartItems.reduce((sum, item) => {
      const mrp = this.getItemMrp(item) * (item.quantity || 1);
      const payable = this.getItemTotal(item);
      return sum + Math.max(0, mrp - payable);
    }, 0);
    return productSavings + (this.promoDiscount || 0);
  }

  get savingsPercent(): number {
    const mrp = this.totalMrp;
    if (!mrp) return 0;
    return Math.round((this.totalSavings / mrp) * 100);
  }

  /** Wallpaper lines billed by the forward flow, for the summary's rate breakdown. */
  get rateBreakdownLines(): any[] {
    return this.cartItems.filter((i: any) => Number(i.site_rate_ex_gst) > 0 && Number(i.site_billable_sqft) > 0);
  }

  /**
   * One line's discount, ex-GST, whole line (× quantity) — `pd.discount`, else its
   * with-GST figure brought back to ex-GST; a collage set's 15% off its listing
   * price. The single source for both `totalDiscount` and the product cards.
   */
  lineDiscountExGst(item: any): number {
    const baseFromWithTax = (withTax: number, taxRate: number): number =>
      Number((withTax / (taxRate ? 1 + taxRate / 100 : 1)).toFixed(2));
    const qty = item.quantity || 1;
    const cfp = this.getCollageFinalPrice(item);
    if (cfp > 0) return baseFromWithTax(Number((cfp * 0.15).toFixed(2)), 18) * qty;
    const pd = item.pricing_data || {};
    return (pd.discount || baseFromWithTax(pd.discount_with_tax || 0, this.lineTaxRate(item))) * qty;
  }

  /**
   * Every OTHER product line — Print & Frame, Wall Art, featured frames, collage
   * sets — priced as the summary totals price them, so the cards add up to the
   * "Items subtotal" exactly: value before discount (ex-GST) → discount → taxable →
   * GST → line total. Wallpaper, sample kits and the measurement visit have their
   * own cards above.
   */
  get otherProductLines(): Array<{
    item: any; name: string; image: string; meta: string; qty: number;
    unitIncl: number; base: number; discount: number; discountPct: number; taxable: number; gst: number; gstPer: number; total: number;
  }> {
    // "PRINT & FRAME" → "Print & Frame", for a quiet one-line description.
    const title = (s: string) => s.toLowerCase().replace(/(^|[\s&/-])([a-z])/g, (_, p, c) => p + c.toUpperCase());
    const shown = new Set<any>([...this.rateBreakdownLines, ...this.visitLines, ...this.sampleLines]);
    return this.cartItems
      .filter((i: any) => !shown.has(i))
      .map((i: any) => {
        const pd = i.pricing_data || {};
        const qty = i.quantity || 1;
        const payable = Number(pd.amount_payable) || 0;
        const tax = Number(pd.tax) || 0;
        // Exactly productTaxable's and gstAmount's per-line terms.
        const taxable = (Number(pd.amount_taxable) || Math.max(0, payable - tax)) * qty;
        const gst = tax * qty;
        const discount = this.lineDiscountExGst(i);
        const size = (i.size_label || i.roll_size || '').toString();
        const name = i.name || 'Item';
        const subType = title(this.itemSubLabels(i)[0] || '');
        // One line: type · sub-type (unless it just repeats the name) · size · qty.
        const meta = [
          title(this.itemTypeLabel(i)),
          subType && subType.toLowerCase() !== name.toLowerCase() ? subType : '',
          size ? this.formatSize(size) : '',
          `Qty ${qty}`,
        ].filter(Boolean).join(' · ');
        return {
          item: i,
          name,
          image: i.image || '',
          meta,
          qty,
          unitIncl: this.getItemSalePrice(i),
          // To the paisa — two paise figures added must not carry float noise.
          base: roundMoney(taxable + discount),
          // The share taken off the value, e.g. the built-in 15% on a Print & Frame line.
          discountPct: discount > 0 && taxable + discount > 0 ? Math.round((discount / (taxable + discount)) * 10000) / 100 : 0,
          discount,
          taxable,
          gst,
          gstPer: this.lineTaxRate(i),
          total: roundMoney(taxable + gst),
        };
      });
  }

  /** The delivery option the customer picked (server-priced), for the summary. */
  get selectedShippingMethod(): any {
    return this.shippingMethods.find((m: any) => m.id === this.selectedShippingId) || null;
  }

  /** What the applied coupon is, in words: "10% off products" / "₹500 off products". */
  get promoLabel(): string {
    const p = this.appliedPromo;
    if (!p) return '';
    const v = Number(p.discount_amount) || 0;
    return p.discount_type_id === 2 ? `${v}% off products` : `₹${v} off products`;
  }

  /** Billed Measurement Service lines (fee + its service GST). */
  get visitLines(): any[] {
    return this.cartItems.filter((i: any) => !!i.pricing_data?.is_visit);
  }

  /** Sample kits — a fixed price, split into its ex-GST value and GST. */
  get sampleLines(): any[] {
    return this.cartItems.filter((i: any) => !!i.is_sample);
  }

  /** Service GST % (installation, measurement), as the server's config sets it. */
  get serviceGstPer(): number {
    const withRate = this.cartItems.find(i => Number(i.pricing_data?.service_gst_per) > 0);
    return withRate ? Number(withRate.pricing_data.service_gst_per) : 0;
  }

  /** Taxable value of the products (ex-GST, after discount). */
  get productTaxable(): number {
    return this.cartItems.reduce((sum, item) => {
      const pd = item.pricing_data || {};
      const payable = Number(pd.amount_payable) || 0;
      const tax = Number(pd.tax) || 0;
      return sum + (Number(pd.amount_taxable) || Math.max(0, payable - tax)) * (item.quantity || 1);
    }, 0);
  }

  /** All GST in the order: products + installation + delivery. */
  get totalTaxAll(): number {
    return this.gstAmount + this.installationTax + (Number(this.shippingTax) || 0);
  }

  get gstAmount(): number {
    return this.cartItems.reduce((sum, item) => {
      const pd = item.pricing_data || {};
      return sum + ((pd.tax || 0) * (item.quantity || 1));
    }, 0);
  }

  /**
   * The GST slab for ONE line, read off the line itself.
   *
   * Replaces `this.lineTaxRate(item)` at the three places the totals used it. Those were
   * only fallbacks — a wallpaper line now always carries its own rate, frozen when
   * the line was quoted — but a fallback that says 18 still asserts a slab, and 18 is
   * not a property of anything: it is one value `paper.gst_per` happens to hold, and
   * the surfaces are on 40 right now.
   *
   * Returns 0 when nothing is known, which reads as "no tax inside this figure".
   * That is the honest answer for an unrated line: inventing a split would put a
   * number in the GST row that no line actually charges. Unreachable for wallpapers,
   * and correct for a booked visit, which is genuinely zero-rated.
   */
  private lineTaxRate(item: any): number {
    const rate = Number(item?.pricing_data?.tax_rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 0;
  }


  /**
   * The GST slabs actually present in this cart, each line's own frozen rate.
   *
   * The summary printed a literal "GST (18%)". The rate is not 18 by nature — it is a
   * per-surface value from `paper.gst_per`, and the amount beside the label was
   * already being summed from each line's real tax, so the label was the only thing
   * still asserting 18. With the test surface on 40% the figure and its caption
   * disagreed.
   *
   * A list, not a single number, because a cart can mix surfaces on different slabs —
   * Glitter sits on 30% where Non-Woven is 18%. Printing one rate beside a mixed
   * figure states a rate that applies to neither line.
   *
   * Zero-rated lines (a booked expert visit) contribute no slab: they carry no GST,
   * so naming one would be wrong.
   */
  get gstSlabs(): number[] {
    const slabs = new Set<number>();
    this.cartItems.forEach(item => {
      const rate = Number(item.pricing_data?.tax_rate);
      if (Number.isFinite(rate) && rate > 0) slabs.add(rate);
    });
    return [...slabs].sort((a, b) => a - b);
  }

  /** "40%", or "18% + 30%" when the cart mixes slabs. Empty when nothing is taxed. */
  get gstLabel(): string {
    return this.gstSlabs
      // Whole rates read as "18%", not "18.00%"; a real fraction keeps its decimals.
      .map(r => (r % 1 === 0 ? String(r) : String(Number(r.toFixed(2)))) + '%')
      .join(' + ');
  }

  get orderSubtotal(): number {
    return this.amountPayable - this.shippingAmount - this.shippingTax;
  }

  // Group cart items by section header (WALL ART / WALLPAPER / PRINT & FRAME)
  // Memoised on the cartItems reference so the *ngFor doesn't rebuild the
  // cart-item DOM (and tear down the pincode input) on every CD cycle.
  private _groupedCartItemsCache: Array<{ label: string; items: any[]; count: number }> = [];
  private _groupedCartItemsRef: any[] | null = null;
  get groupedCartItems(): Array<{ label: string; items: any[]; count: number }> {
    if (this._groupedCartItemsRef === this.cartItems) return this._groupedCartItemsCache;
    const buckets: Record<string, any[]> = {
      'WALL ART': [],
      'PRINT & FRAME': [],
      'WALLPAPER': [],
    };
    for (const item of this.cartItems) {
      if (this.isWallArtItem(item)) buckets['WALL ART'].push(item);
      else if (this.isPrintFrameItem(item)) buckets['PRINT & FRAME'].push(item);
      else buckets['WALLPAPER'].push(item);
    }
    this._groupedCartItemsCache = Object.entries(buckets)
      .filter(([, items]) => items.length > 0)
      .map(([label, items]) => ({
        label,
        items,
        count: items.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
      }));
    this._groupedCartItemsRef = this.cartItems;
    return this._groupedCartItemsCache;
  }

  // Addresses
  // Safe city display — handles both string and object
  displayCity(addr: any): string {
    if (!addr) return '';
    const city = addr.city;
    if (!city) return '';
    if (typeof city === 'string') return city;
    let display = city.name || '';
    if (city.state && city.state.name) {
      display += `, ${city.state.name}`;
      if (city.state.country && city.state.country.name) {
        display += `, ${city.state.country.name}`;
      }
    }
    return display;
  }

  loadAddresses() {
    this.orderService.getAddresses().subscribe({
      next: (res: any) => {
        this.savedAddresses = res?.data || res || [];
        if (this.savedAddresses.length > 0) {
          // Keep current selection if still valid, otherwise pick default
          const currentStillExists = this.selectedShippingAddressId && this.savedAddresses.find(a => a.id === this.selectedShippingAddressId);
          if (!currentStillExists) {
            const def = this.savedAddresses.find(a => a.is_default);
            this.selectedShippingAddressId = (def || this.savedAddresses[0]).id!;
            this.selectedBillingAddressId = this.selectedShippingAddressId;
          }
          this.fetchShipping(this.selectedShippingAddress?.zipcode || '');
        } else {
          this.selectedShippingAddressId = null;
          this.selectedBillingAddressId = null;
        }
      },
      error: () => {}
    });
  }

  selectShippingAddr(id: number) {
    this.selectedShippingAddressId = id;
    const addr = this.savedAddresses.find(a => a.id === id);
    if (addr?.zipcode) this.fetchShipping(addr.zipcode);
  }

  selectBillingAddr(id: number) {
    this.selectedBillingAddressId = id;
  }

  openAddressModal() {
    this.addressEditData = null;
    this.showAddressModal = true;
    this.scrollToFormElement('shipping-address-form');
  }

  editAddress(addr: any) {
    this.addressEditData = { ...addr };
    this.showAddressModal = true;
    this.scrollToFormElement('shipping-address-form');
  }

  private scrollToFormElement(id: string) {
    // Wait for the *ngIf-mounted form to render before scrolling.
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  onAddressSaved(event: { address: any; isEdit: boolean }) {
    const saved = event.address;
    this.selectedShippingAddressId = saved.id;
    if (this.billingSameAsShipping) {
      this.selectedBillingAddressId = saved.id;
    }
    this.showAddressModal = false;
    this.addressEditData = null;
    this.state.showToast(event.isEdit ? 'Address updated' : 'Address added', 'success');
    // Reload all addresses from backend to get fresh data with city/state
    this.loadAddresses();
  }

  openBillingAddressModal() {
    this.billingAddressEditData = null;
    this.showBillingAddressModal = true;
    this.scrollToFormElement('billing-address-form');
  }

  editBillingAddress(addr: any) {
    this.billingAddressEditData = { ...addr };
    this.showBillingAddressModal = true;
    this.scrollToFormElement('billing-address-form');
  }

  onBillingAddressSaved(event: { address: any; isEdit: boolean }) {
    const saved = event.address;
    this.selectedBillingAddressId = saved.id;
    this.showBillingAddressModal = false;
    this.billingAddressEditData = null;
    this.state.showToast(event.isEdit ? 'Billing address updated' : 'Billing address added', 'success');
    this.loadAddresses();
  }

  // Guest checkout
  // Guest city autocomplete
  onGuestCityInput(value: string) {
    this.guestCityText = value;
    this.guestSelectedCity = null;
    this.guestShippingForm.city_id = 0;
    if (value.length < 2) { this.guestCities = []; this.showGuestCityDropdown = false; return; }
    this.guestCityLoading = true;
    this.orderService.searchCities(value).subscribe({
      next: (res: any) => {
        this.guestCities = res?.data || res || [];
        this.showGuestCityDropdown = this.guestCities.length > 0;
        this.guestCityLoading = false;
      },
      error: () => { this.guestCityLoading = false; }
    });
  }

  selectGuestCity(city: any) {
    this.guestSelectedCity = city;
    this.guestShippingForm.city = city;
    this.guestShippingForm.city_id = city.id;
    this.guestCityText = city.name + (city.state ? ` (${city.state.name})` : '');
    this.showGuestCityDropdown = false;
    this.guestCities = [];
  }

  hideGuestCityDropdown() {
    setTimeout(() => this.showGuestCityDropdown = false, 200);
  }

  saveGuestShipping() {
    const f = this.guestShippingForm;
    if (!f.fullname || !f.mobile || !f.email || !f.address || !f.zipcode) {
      this.guestFormError = 'Please fill all required fields (including email)';
      return;
    }
    if (!this.guestSelectedCity || !this.guestSelectedCity.id) {
      this.guestFormError = 'Please select a valid city from the dropdown';
      return;
    }
    if (String(f.mobile).length < 10) {
      this.guestFormError = 'Mobile must be 10 digits';
      return;
    }
    if (String(f.zipcode).length < 6) {
      this.guestFormError = 'Zipcode must be 6 digits';
      return;
    }
    this.guestFormError = '';
    this.addressLoading = true;

    // Build clean payload — backend sets customer_id & email from user_id
    const guestPayload: any = {
      fullname: f.fullname,
      mobile: f.mobile,
      email: f.email || '',
      address: f.address,
      zipcode: f.zipcode,
      city_id: this.guestSelectedCity.id,
      company_name: f.company_name || '',
      gst: f.gst || '',
      pan: f.pan || '',
      user_id: this.auth.currentUser?.id || 0,
    };

    this.orderService.addAddress(guestPayload).subscribe({
      next: (res: any) => {
        const saved = res?.data || res;
        this.guestShippingAddressId = saved.id;
        this.selectedShippingAddressId = saved.id;
        this.selectedBillingAddressId = saved.id;
        this.savedAddresses = [saved];
        this.addressLoading = false;
        this.guestStep = 2;
        this.fetchShipping(f.zipcode);
      },
      error: () => {
        this.guestFormError = 'Failed to save address. Please try again.';
        this.addressLoading = false;
      }
    });
  }

  saveGuestBillingAndProceed() {
    if (this.billingSameAsShipping) {
      // Billing = shipping address
      this.selectedBillingAddressId = this.selectedShippingAddressId || this.guestShippingAddressId;
    } else {
      // Validate guest billing form
      const f = this.guestBillingForm;
      if (!f.fullname || !f.mobile || !f.address || !f.city || !f.zipcode) {
        this.guestFormError = 'Please fill all billing address fields.';
        return;
      }
      // Save billing address
      this.addressLoading = true;
      const billingPayload: any = {
        fullname: f.fullname,
        mobile: f.mobile,
        address: f.address,
        zipcode: f.zipcode,
        city_id: f.city_id || 0,
        user_id: this.auth.currentUser?.id || 0,
      };
      this.orderService.addAddress(billingPayload).subscribe({
        next: (res: any) => {
          const saved = res?.data || res;
          this.guestBillingAddressId = saved.id;
          this.selectedBillingAddressId = saved.id;
          this.addressLoading = false;
          this.placeOrder();
        },
        error: () => {
          this.guestFormError = 'Failed to save billing address.';
          this.addressLoading = false;
        }
      });
      return;
    }
    this.placeOrder();
  }

  deleteAddress(id: number) {
    // Remove locally first for instant UI
    this.savedAddresses = this.savedAddresses.filter(a => a.id !== id);
    if (this.selectedShippingAddressId === id) {
      this.selectedShippingAddressId = this.savedAddresses.length > 0 ? this.savedAddresses[0].id! : null;
    }
    if (this.selectedBillingAddressId === id) {
      this.selectedBillingAddressId = this.selectedShippingAddressId;
    }

    // Delete from backend
    this.orderService.deleteAddress(id).subscribe({
      next: () => {
        this.state.showToast('Address deleted', 'info');
        // Reload to stay in sync
        this.loadAddresses();
      },
      error: () => {
        this.state.showToast('Failed to delete address', 'info');
        this.loadAddresses();
      }
    });
  }

  // Shipping
  /**
   * The delivery options, from the server.
   *
   * This used to make two calls to the old backend and then do the pricing
   * itself, which produced three different numbers for one delivery:
   *
   *     option card      shipping_method.amount                       ₹150
   *     order summary    that + getMaxTaxRate() × amount              ₹177
   *     the actual order amount + the server's own tax rate           ₹168
   *
   * Worse, `getMaxTaxRate()` read `pricing_data.tax_rate` off the cart, so the
   * DELIVERY charge moved with the paper's GST slab — 12% on a new-shape row, 18%
   * on an old one. And the second of those two calls, `getShippingAmt`, answers
   * 404 in both backends and always has, so the pincode branch never ran at all.
   *
   * One call now. Each option arrives with `total` — what the customer pays and
   * what will be charged — plus the tax contained inside it, whether it is free,
   * and why. Nothing here recomputes any of it.
   *
   * The `pincode` argument is kept in the signature because the callers pass it
   * and because the server picks the rate set from the delivery address; it is
   * resolved there from the cart's own address rather than trusted from here.
   */
  fetchShipping(pincode: string) {
    if (!pincode || pincode.length < 6) return;
    this.shippingLoading = true;

    // Recorded at REQUEST time, not on the response: it is what this quote is
    // about, and it is what stops the refetch check below from firing again on
    // the calculateTotals() that follows.
    this.shippingQuotedFor = { cart: this.cartSignature(), pincode };

    const scope = this.buyNowMode && this.buyNowItemIds.length ? this.buyNowItemIds : null;

    this.orderService.getSiteShippingOptions(scope).subscribe({
      next: (res: any) => {
        this.shippingMethods = (res?.data?.options || []).map((o: any) => ({
          id: o.id,
          name: this.deliveryLabel(o.name),
          description: o.description || '',
          days: o.days || '',
          // Ex-tax, for the books. `total` is the figure to show.
          amount: Number(o.amount) || 0,
          total: Number(o.total) || 0,
          tax: Number(o.tax) || 0,
          free: !!o.free,
          reason: o.reason ?? null,
        }));

        // Keep the customer's choice across a refetch (the address changing must
        // not silently move them off Fast Delivery); otherwise lead with the
        // first option, which the server returns cheapest-first.
        const stillThere = this.shippingMethods.some(m => m.id === this.selectedShippingId);
        if (!stillThere) {
          this.selectedShippingId = this.shippingMethods[0]?.id ?? null;
        }

        this.applySelectedShipping();
        this.shippingLoading = false;
        this.calculateTotals();
      },
      error: () => {
        // No options rather than a guessed price. Delivery then costs nothing and
        // the order is placed without a method, which the server prices at zero
        // too — the page and the charge still agree.
        this.shippingMethods = [];
        this.selectedShippingId = null;
        this.shippingAmount = 0;
        this.shippingTax = 0;
        this.shippingBaseAmount = 0;
        this.isShippingFree = false;
        this.shippingLoading = false;
        this.calculateTotals();
      }
    });
  }

  /**
   * Display label for a delivery method.
   *
   * The table calls it "Traditional Delivery"; the site has always shown
   * "Standard-Delivery". A rename here only, so the id and the server's own
   * matching are untouched.
   */
  private deliveryLabel(name: string): string {
    return (name || '').trim().toLowerCase() === 'traditional delivery'
      ? 'Standard-Delivery'
      : (name || '');
  }

  /**
   * Adopt the selected option's figures. No arithmetic.
   *
   * `applyShippingOffer()` and `getMaxTaxRate()` used to live here and are gone:
   * the free-delivery rule and the tax split are the server's, driven by
   * `settings` rows, so a second opinion on this page could only ever disagree
   * with the charge.
   *
   * `shippingAmount` + `shippingTax` still add to the total, which is what
   * calculateTotals() and the summary already sum — so the displayed figure is
   * the server's total by construction.
   */
  private applySelectedShipping() {
    const m = this.shippingMethods.find(x => x.id === this.selectedShippingId);

    this.shippingAmount = Number(m?.amount) || 0;
    this.shippingTax = Number(m?.tax) || 0;
    this.shippingBaseAmount = Number(m?.total) || 0;
    this.isShippingFree = m ? !!m.free : false;
  }

  /** The selected option's total — what the summary shows and the card echoes. */
  get shippingTotal(): number {
    return Number(this.shippingAmount) + Number(this.shippingTax);
  }

  /**
   * What the delivery quote depends on: which lines, and how many of each.
   *
   * Sorted so a reordered cart is not mistaken for a changed one. Installation
   * and gift toggles are excluded — they do not enter the product subtotal the
   * server measures the free-delivery threshold against.
   */
  private cartSignature(): string {
    return this.cartItems
      .map((i: any) => `${i.id}:${i.quantity || 1}`)
      .sort()
      .join('|');
  }

  /**
   * Re-quote delivery when the cart has changed since the last quote.
   *
   * This is what makes the ₹499 free-delivery threshold work as the cart moves.
   * Removing an item can drop a cart below it and reinstate the charge; adding
   * one can cross it and make delivery free. The page does not test the threshold
   * itself — it cannot, the number lives in a `settings` row on the server — so
   * it asks for a fresh quote and the server re-decides.
   *
   * Guarded three ways so it cannot loop or thrash:
   *   - nothing to do until a first quote exists (an address is needed for one);
   *   - never while a quote is already in flight;
   *   - and the signature recorded at request time equals the current one by the
   *     time the response lands, so the calculateTotals() it triggers stops here.
   */
  private refetchShippingIfCartChanged(): void {
    if (this.shippingLoading || !this.shippingQuotedFor) {
      return;
    }
    if (this.cartSignature() === this.shippingQuotedFor.cart) {
      return;
    }

    this.fetchShipping(this.shippingQuotedFor.pincode);
  }

  selectShipping(method: ShippingMethod) {
    this.selectedShippingId = method.id;
    this.applySelectedShipping();
    this.calculateTotals();
  }

  // Promo / Coupon
  openPromoDialog() {
    if (!this.auth.isLoggedIn) {
      this.auth.requestLogin();
      return;
    }
    this.showPromoDialog = true;
  }

  onPromoApplied(promo: any) {
    this.appliedPromo = promo;
    this.promoCode = promo.code;
    this.promoApplied = true;

    /*
     * The discount is computed on the TAX-INCLUSIVE cart total — the same base
     * the server uses (SitePromoService::amount → productIncl × pct/100), and the
     * same base `amountPayable` is built from below.
     *
     * This used to apply the percentage on the EX-TAX net (totalAmount −
     * totalDiscount) while `amountPayable` subtracted it from the INCLUSIVE
     * cartPayable — two different bases in one sum. A 10% coupon on a ₹20,880
     * (incl) / ₹17,694.92 (ex-tax) cart previewed ₹1,769.49 off but the server
     * charged ₹2,088.00, so "You pay" jumped when the customer clicked Pay.
     *
     * `cartSubtotal` is that inclusive base (set in calculateTotals). The discount
     * is capped at it so a flat coupon larger than the cart, or a 100% one, cannot
     * drive the total negative — mirroring the server's own cap.
     */
    // Sample kits are a FIXED price and take no coupon — excluded from the base,
    // as the server does (SiteOrderService sizes it on the goods only).
    // …and the Measurement Service, a service like installation (coupons discount goods).
    const base = Math.max(0, this.cartSubtotal - [...this.sampleLines, ...this.visitLines].reduce((s: number, i: any) =>
      s + (Number(i.pricing_data?.amount_payable) || 0) * (i.quantity || 1), 0));
    if (promo.discount_type_id === 1) {
      // Flat amount, in rupees — as-is, only capped at the cart.
      this.promoDiscount = Math.min(roundMoney(Number(promo.discount_amount) || 0), base);
    } else if (promo.discount_type_id === 2) {
      // Percentage of the inclusive products, whole rupees (the server's rule).
      this.promoDiscount = Math.min(roundMoney(base * (Number(promo.discount_amount) || 0) / 100), base);
    }
    this.calculateTotals();
    this.state.showToast(`Coupon "${promo.code}" applied successfully!`, 'success');
  }

  removePromo() {
    this.appliedPromo = null;
    this.promoCode = '';
    this.promoApplied = false;
    this.promoDiscount = 0;
    this.calculateTotals();
    this.state.showToast('Coupon removed', 'info');
  }

  // Installation
  toggleInstallation(itemId: string) {
    if (this.installationExpanded.has(itemId)) {
      this.installationExpanded.delete(itemId);
    } else {
      this.installationExpanded.add(itemId);
    }
  }

  // Checkbox-driven installation toggle.
  // Checked   → if pincode already validated OR backend already has installation,
  //             apply immediately; otherwise expand the pincode form.
  // Unchecked → remove installation charge from totals.
  /**
   * Wipes every piece of installation state an item holds — the applied flag, the
   * open form, the typed pincode and its lookup result. Leaving any of it behind
   * makes the next attempt resume mid-flow: a stale pincode reappears prefilled,
   * and a stale 'available' status skips the form and jumps straight to the terms.
   */
  /**
   * Clear installation on a line — locally AND on the row.
   *
   * The local flags alone were never enough: the charge lives in the cart row's
   * `pricing_detail`, so unticking without telling the server left it billed at
   * checkout. The write is fire-and-forget on failure: the refetch will show the true
   * state, and a toast about a failed removal on top of an already-confusing screen
   * helps nobody.
   */
  /**
   * Clear installation on a line — locally AND on the row.
   *
   * The local flag flips first on purpose: it is what the customer clicked, and
   * `installationAmount` honours a pending `false`, so the checkbox and the order
   * summary drop the charge in the same frame. The spinner then says the row is still
   * settling with the server, which is the honest state — instant on screen, confirmed
   * a moment later.
   */
  private async resetItemInstallation(itemId: string): Promise<void> {
    itemId = String(itemId);
    const item = this.cartItems.find(i => String(i.id) === String(itemId));
    const hadCharge = (item?.pricing_data?.installation_amount || 0) > 0;

    this.itemInstallation[itemId] = false;
    this.installationExpanded.delete(itemId);
    this.itemPincode[itemId] = '';
    this.itemPincodeStatus[itemId] = '';
    delete this.itemInstallQuote[itemId];
    // Summary follows the tick immediately — this is the glitch this fixes.
    this.calculateTotals();

    if (!hadCharge) return;
    const cartId = this.cartIdOf(item);
    if (!cartId) return;

    this.installationBusy[itemId] = true;
    try {
      await this.state.apiUpdateCartServices(cartId, { installation: false }).toPromise();
    } catch {
      // The server still has the installation on the line, so the order WILL bill
      // it. Drop the local "removed" flag so the summary shows the charge again —
      // leaving it set hid a charge Razorpay then collected — and say so.
      delete this.itemInstallation[itemId];
      this.state.showToast('Could not remove installation — it is still on this item.', 'info');
    } finally {
      delete this.installationBusy[itemId];
      this.state.loadCartFromApi(true);
    }
  }

  onInstallationCheckboxToggle(itemId: string, input: HTMLInputElement) {
    itemId = String(itemId);
    if (!input.checked) {
      // Not awaited on purpose: it flips the tick and recomputes the summary
      // synchronously before it ever touches the network, and handles its own
      // failure. `void` states that rather than leaving a bare floating promise.
      void this.resetItemInstallation(itemId);
      return;
    }
    const item = this.cartItems.find(i => String(i.id) === String(itemId));
    // The row is the authority on whether a charge is already on this line.
    const alreadyOn = !!item && this.hasInstallation(item);
    const pincodeAlreadyOk = this.itemPincodeStatus[itemId] === 'available';
    if (alreadyOn || pincodeAlreadyOk) {
      // Serviceability is already known — this tick is the charge-add action, so ask
      // for the agreement before applying it. The box is cleared by hand because the
      // bound value doesn't change here, so Angular won't reset it.
      input.checked = false;
      this.requestInstallation(itemId);
    } else {
      // Need the pincode flow first. No charge until the agreement is accepted.
      this.installationExpanded.add(itemId);
    }
  }

  /**
   * Opens the installation agreement. Called from the points where the user is
   * actually adding the installation charge — never from the pincode lookup,
   * which only reports serviceability.
   */
  requestInstallation(itemId: string) {
    itemId = String(itemId);
    this.pendingInstallationItemId = itemId;
    this.showInstallationAgreement = true;
  }

  /**
   * Editing the pincode invalidates the previous lookup, so the "Add installation"
   * button can't stay on screen for a pincode that was never verified.
   */
  onItemPincodeChanged(itemId: string, value: string) {
    itemId = String(itemId);
    this.itemPincode[itemId] = value;
    if (this.itemPincodeStatus[itemId]) {
      this.itemPincodeStatus[itemId] = '';
      delete this.itemInstallQuote[itemId];
    }
  }

  /**
   * Is installation available at this pincode, and at what rate?
   *
   * This read `res.exist`, which the endpoint no longer sends. `exist` was the OLD
   * backend's answer — a bare boolean. The new one answers `serviceable` and comes
   * with the city, the ₹/sq ft and the minimum charge, so this was reporting
   * "unavailable" for every pincode and installation could not be added here at all.
   *
   * `serviceable` is tested for `true` explicitly: `null` means the postal lookup
   * was unreachable, which is not-yet-known rather than not-serviceable, and telling
   * a customer in a serviced city that we do not cover them is the worse mistake.
   */
  checkItemInstallation(itemId: string) {
    itemId = String(itemId);
    const pincode = this.itemPincode[itemId];
    if (!pincode || pincode.length < 6) return;

    const item = this.cartItems.find(i => String(i.id) === String(itemId));
    if (item && (item.surface_name || '').toLowerCase().includes('self adhesive')) {
      this.itemPincodeStatus[itemId] = 'self-adhesive';
      this.itemInstallation[itemId] = false;
      this.calculateTotals();
      return;
    }

    this.itemPincodeStatus[itemId] = 'checking';
    this.wallpaperService.checkInstallationPincode(pincode).subscribe({
      next: (res: any) => {
        // A city can be serviceable and still have no ₹/sq ft set, so the rate is
        // part of the answer, not an assumption on top of it.
        if (res?.serviceable === true && res?.rate_per_sqft) {
          this.itemPincodeStatus[itemId] = 'available';
          // Held so the card can name the rate and city before the charge is added.
          this.itemInstallQuote[itemId] = {
            rate: Number(res.rate_per_sqft) || 0,
            city: res.city || null,
            // The fixed charge and the total sq ft it covers (all walls together).
            fixed: Number(res.min_charge) > 0 && Number(res.flat_upto_sqft) > 0 ? Number(res.min_charge) : null,
            uptoSqft: Number(res.flat_upto_sqft) > 0 ? Number(res.flat_upto_sqft) : null,
          };
        } else {
          this.itemPincodeStatus[itemId] = 'unavailable';
          this.itemInstallation[itemId] = false;
          this.calculateTotals();
        }
      },
      error: () => {
        this.itemPincodeStatus[itemId] = 'unavailable';
        this.itemInstallation[itemId] = false;
        this.calculateTotals();
      }
    });
  }

  /**
   * Terms accepted — PERSIST the installation, then let the server's own figure drive
   * the totals.
   *
   * This used to set `itemInstallation[id] = true` and stop there: the charge existed
   * only in this component, computed from a client-side ₹20/sq.ft, and vanished on
   * reload. The order then billed something the customer had never seen. Now the cart
   * row is updated server-side and the cart refetched, so the charge on screen is the
   * charge that is stored.
   */
  /**
   * Terms accepted — persist the installation and let the server's figure drive the
   * totals.
   *
   * async/await rather than a nested subscribe, so the sequence reads in order:
   * mark the line busy, ask the server, refetch, clear. The busy flag is what the
   * customer sees — a spinner on the row and a disabled checkbox — because between
   * the click and the answer there was previously no signal at all.
   */
  async onInstallationAgreed(): Promise<void> {
    this.showInstallationAgreement = false;
    const itemId = String(this.pendingInstallationItemId);
    this.pendingInstallationItemId = '';
    if (!itemId) return;

    const item = this.cartItems.find(i => String(i.id) === String(itemId));
    const cartId = this.cartIdOf(item);
    if (!cartId) return;   // unresolved row: ask the server nothing

    const pincode = this.itemPincode[itemId] || item?.pricing_data?.pincode || '';
    // Whether this line ALREADY carries a charge. Decides the failure path: a failed
    // first add rolls the tick back, a failed pincode CHANGE must not destroy the
    // installation still validly on the line.
    const hadCharge = (item?.pricing_data?.installation_amount || 0) > 0;

    this.installationBusy[itemId] = true;
    this.installationExpanded.delete(itemId);

    try {
      await this.state.apiUpdateCartServices(cartId, { installation: true, pincode }).toPromise();
      // Only now is the tick true — the amount comes back with the refetch, so
      // claiming it earlier would tick a box with no figure beside it.
      this.itemInstallation[itemId] = true;
      this.state.showToast(hadCharge ? 'Installation updated' : 'Installation added', 'success');
      this.state.loadCartFromApi(true);
    } catch (err: any) {
      this.state.showToast(
        err?.error?.message
        || err?.error?.errors?.installation?.[0]
        || err?.error?.errors?.pincode?.[0]
        || 'Could not add installation for this pincode.',
        'info'
      );
      if (hadCharge) {
        // Leave the existing, still-valid charge alone; reopen the form so the
        // customer can try a different pincode.
        delete this.itemInstallation[itemId];
        this.installationExpanded.add(itemId);
        this.state.loadCartFromApi(true);
      } else {
        this.itemInstallation[itemId] = false;
        this.installationExpanded.add(itemId);
        this.calculateTotals();
      }
    } finally {
      delete this.installationBusy[itemId];
    }
  }

  /**
   * Terms declined (Cancel or clicking away) — installation must not be charged.
   * The whole form is reset, not just hidden, so ticking the checkbox again starts
   * at the pincode step instead of re-opening the terms on a leftover 'available'.
   */
  onInstallationDeclined() {
    this.showInstallationAgreement = false;
    if (this.pendingInstallationItemId) {
      this.resetItemInstallation(this.pendingInstallationItemId);
      this.calculateTotals();
    }
    this.pendingInstallationItemId = '';
  }

  toggleDetail(itemId: string) {
    if (this.detailExpanded.has(itemId)) {
      this.detailExpanded.delete(itemId);
    } else {
      this.detailExpanded.add(itemId);
    }
  }

  // Cart actions

  updateQuantity(id: string, delta: number) {
    // One quantity change at a time: cartBusy stays on (blocking overlay + disabled
    // controls) until the reload has repainted, so rapid clicks cannot fire
    // overlapping requests whose out-of-order responses would leave the Order
    // Summary stale. The service's newest-wins reload guard is the backstop.
    if (this.cartBusy) return;
    const item = this.cartItems.find(i => String(i.id) === String(id));
    if (!item) return;
    const newQty = Math.max(1, (item.quantity || 1) + delta);
    if (newQty === (item.quantity || 1)) return;

    // The CART id — the new endpoint is addressed by the cart row and owns
    // rescaling the stored line price.
    const cartId = this.cartIdOf(item);
    if (!cartId) return;

    this.state.beginCartMutation();
    // Refetch on success: the line price and, with it, every summary figure is
    // recomputed server-side, so reading it back keeps the page honest instead of
    // scaling numbers locally. loadCartFromApi clears cartBusy once the fresh state
    // is painted (never merely when the HTTP response returns).
    this.state.apiUpdateQuantity(cartId, newQty).subscribe({
      next: () => this.state.loadCartFromApi(true),
      error: () => {
        this.state.showToast('Could not update the quantity. Please try again.', 'info');
        this.state.loadCartFromApi(true);
      },
    });
  }

  removeItem(id: string) {
    // Don't remove while a quantity reload is mid-flight — it would race the refetch.
    if (this.cartBusy) return;
    const item = this.cartItems.find(i => String(i.id) === String(id));
    const cartId = item?.cart_id || item?.id || id;

    // Remove locally + from backend
    this.cartItems = this.cartItems.filter(i => String(i.id) !== String(id));
    this.calculateTotals();
    this.state.showToast('Item removed from cart', 'wishlist-remove');

    if (cartId) {
      // The service re-reads the cart afterwards, so a failed delete puts the line
      // back — say so rather than let it reappear unexplained.
      this.state.apiRemoveCartItem(Number(cartId)).subscribe({
        error: () => this.state.showToast('Could not remove the item. Please try again.', 'info'),
      });
    }
  }

 moveToWishlist(item: any) {
    const raw = item.calculated_total
      || item.pricing_data?.amount_payable
      || item.pricing_data?.total_with_tax
      || item.price
      || 0;
    const os = item.on_sale || {};
    const productSlug = (os.product || os.product_slug || '').toString().toLowerCase();
    const productTypeSlug = (os.product_type?.slug || '').toString().toLowerCase();
    this.state.toggleWishlist(String(item.id), {
      id: String(item.id),
      name: item.name,
      image: item.image || item.list_thumbnail,
      price: Number(raw) || 0,
      wallpaper_type: item.wallpaper_type,
      wallpaper_id: item.wallpaper_id,
      collection_slug: item.collection_slug || '',
      // on_sale items store the title_slug in on_sale_title_slug; collage items use collage_slug
      slug: item.collage_slug || item.on_sale_title_slug || item.slug,
      product_slug: productSlug || undefined,
      product_type_slug: productTypeSlug || undefined,
    });
    // The DELETE, not the local filter. `state.removeFromCart()` only edited the
    // local list, so "Move to wishlist" wishlisted the item and left it in the
    // cart — it came back on the next load, and the customer was billed for
    // something they had moved out.
    const cartId = Number(item.cart_id || item.id);
    if (cartId) {
      this.state.apiRemoveCartItem(cartId).subscribe({
        error: () => {
          this.state.showToast('Could not remove the item from your cart.', 'info');
          this.state.loadCartFromApi(true);
        },
      });
    }
  }

  // Place Order & Pay
  placeOrder() {
    this.paymentError = '';

    // Validation
    if (this.cartItems.length === 0) {
      this.paymentError = 'Your cart is empty.';
      return;
    }

    const shippingAddrId = this.selectedShippingAddressId || this.guestShippingAddressId;
    if (!shippingAddrId) {
      this.paymentError = 'Please add a shipping address first.';
      return;
    }

    if (!this.selectedShippingId && this.shippingMethods.length > 0) {
      this.paymentError = 'Please select a shipping method.';
      return;
    }

    if (this.amountPayable <= 0 && !this.appliedPromo) {
      this.paymentError = 'Invalid order amount. Please check your cart.';
      return;
    }

    this.orderLoading = true;

    const billingAddrId = this.selectedBillingAddressId || shippingAddrId;

    /*
     * Everything the server needs, and nothing it should not take from us.
     *
     * The old payload carried `customer_id`, `customer_discount`,
     * `credit_amt_used`, `payment_method` and `is_guest` — all of them decided in
     * the browser. They are gone: the token says who is ordering, and the cart
     * decides the money. `promo_id` is gone too, because a promo has to be priced
     * server-side to mean anything.
     */
    const orderPayload = {
      address_id: Number(billingAddrId),
      shipping_address_id: Number(shippingAddrId) || null,
      shipping_method_id: this.selectedShippingId || null,
      // Buy Now: order only the clicked row(s). Omitted for normal checkout, which
      // orders the whole cart — the server still filters these against the
      // caller's own rows, so a foreign cart id selects nothing.
      cart_list: this.buyNowMode && this.buyNowItemIds.length ? this.buyNowItemIds : null,
      // The code only. If the server refuses it, the order is NOT created and the
      // reason comes back on `promo_code` — so the customer learns the coupon did
      // not apply before any money is asked for, rather than seeing a discount on
      // screen and a different figure in the payment window.
      promo_code: this.promoApplied ? (this.promoCode || null) : null,
    };

    this.orderService.placeSiteOrder(orderPayload).subscribe({
      next: (res: any) => {
        const order = res?.data || res;
        if (order?.id) {
          // A total that moved by more than a rupee is SHOWN, and the customer pays
          // on a second press (the server reuses this same order) — opening the
          // payment window straight away cleared the notice before it was seen.
          if (this.reconcileWithServer(order)) {
            this.orderLoading = false;
            return;
          }
          this.initiateRazorpay(order);
        } else {
          this.paymentError = this.checkoutError(res, 'Could not create your order. Please try again.');
          this.orderLoading = false;
        }
      },
      error: (err: any) => {
        this.paymentError = this.checkoutError(err?.error, 'Could not create your order. Please try again.');
        this.orderLoading = false;
      }
    });
  }

  /**
   * The reason a checkout call failed, in words a customer can act on.
   *
   * Reads Laravel's `errors` map first — that is where the real per-field reason
   * is ("Your cart could not be priced", "That delivery address could not be
   * found on your account"). The old handler read only `message` and appended
   * "(status 422)", which told the customer nothing and sent them to support.
   */
  /**
   * Make this page agree with the order that was just written.
   *
   * Razorpay charges `grand_total` off that row, so any figure still on screen
   * that disagrees with it is a surprise waiting at the payment window. This is
   * where a coupon actually lands: the page applies it locally for instant
   * feedback, the SERVER decides what it is worth, and this adopts the server's
   * answer before the modal opens.
   *
   * It is deliberately not a blocking prompt. A refused coupon never gets this
   * far — the server rejects the order outright with a reason on `promo_code`, so
   * by the time we are here the only thing that can differ is the SIZE of a
   * discount the server accepted, and showing the real number is enough.
   */
  private reconcileWithServer(order: any): boolean {
    const serverTotal = Number(order.grand_total) || 0;
    const shownTotal = Number(this.amountPayable) || 0;

    if (order.promo) {
      this.promoApplied = !!order.promo.applied;
      this.promoDiscount = Number(order.promo.discount) || 0;
      this.promoCode = order.promo.code || '';
      if (!order.promo.applied) {
        this.appliedPromo = null;
      }
    }

    /*
     * Delivery, from the server.
     *
     * The free-shipping rule used to live only in `applyShippingOffer()` on this
     * page, which meant the page could label Standard "Free" while the rate card
     * — and therefore the order — said ₹150. The rule now lives in
     * SiteShippingService, driven by `settings` rows, so this adopts its answer
     * rather than keeping a second opinion.
     */
    if (order.delivery) {
      this.shippingAmount = Number(order.delivery.amount) || 0;
      this.shippingTax = Number(order.delivery.tax) || 0;
      this.isShippingFree = !!order.delivery.free;
      this.selectedShippingId = order.delivery.method_id ?? this.selectedShippingId;
    } else {
      this.shippingAmount = Number(order.shipping_amount) || 0;
    }

    this.installationAmount = Number(order.installation_amount) || 0;
    this.installationTax = Number(order.installation_tax) || 0;
    this.amountPayable = serverTotal;

    // Worth telling the customer about rather than silently swapping the number
    // under them. A rupee of rounding is not; anything larger is.
    if (Math.abs(serverTotal - shownTotal) > 1) {
      this.paymentError = `Your total has been updated to ₹${serverTotal.toLocaleString('en-IN')} — please review it and press Pay again.`;
      return true;
    }
    return false;
  }

  private checkoutError(payload: any, fallback: string): string {
    const errors = payload?.errors;
    if (errors && typeof errors === 'object') {
      const all = ([] as string[]).concat(...(Object.values(errors) as string[][]));
      if (all.length) return all.join(' · ');
    }
    return payload?.msg || payload?.message || fallback;
  }

  private clearCart() {
    this.state.cart$.next([]);
    this.state.cartRaw$.next([]);
    this.state.cartLoaded$.next(false);
  }

  /**
   * Collect payment for an order that has already been written.
   *
   * Replaces initiatePayU(). The differences that matter:
   *
   *   - the amount is NOT computed here. PayU was sent `this.amountPayable`, a
   *     figure this page worked out, signed with a salt this bundle carried. Now
   *     the server reads it from the `order` row and Razorpay enforces it from
   *     the order id — the browser has no say in what is charged.
   *   - there is no hidden form and no full-page POST. The modal opens in place,
   *     so a customer who dismisses it lands back on their own checkout with
   *     their cart intact rather than on a gateway error page.
   *   - the cart is still NOT cleared here. It is cleared server-side when money
   *     actually arrives, so a declined card leaves everything ready to retry.
   */
  private initiateRazorpay(order: any) {
    const orderPk = Number(order.id);

    this.orderService.razorpayStart(orderPk).subscribe({
      next: (res: any) => {
        const cfg = res?.data;
        if (!cfg?.razorpay_order_id || !cfg?.key) {
          this.paymentError = 'Could not start the payment. Please try again in a moment.';
          this.orderLoading = false;
          return;
        }

        this.openRazorpayCheckout(orderPk, cfg);
      },
      error: (err: any) => {
        this.paymentError = this.checkoutError(err?.error, 'Could not start the payment. Please try again.');
        this.orderLoading = false;
      }
    });
  }

  /**
   * Razorpay's Checkout script, loaded on demand.
   *
   * Deliberately not a <script> tag in index.html: this is a payments SDK that
   * every visitor would otherwise download on the homepage, and only the checkout
   * page ever needs it. Resolves immediately if it is already present, so a retry
   * after a declined card does not fetch it twice.
   */
  private loadRazorpayScript(): Promise<boolean> {
    if ((window as any).Razorpay) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
      const existing = document.getElementById('razorpay-checkout-js') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => resolve(false));
        return;
      }

      const s = document.createElement('script');
      s.id = 'razorpay-checkout-js';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  }

  /**
   * Open Razorpay's own checkout — the standard overlay modal.
   *
   * Not embedded. An earlier version pointed `parent` at a container of ours to
   * enlarge the panel; it looked worse, not better, so this is back to
   * Razorpay's overlay, which is the polished, responsive, mobile-tested view
   * they maintain. The only thing our page decides now is what happens AFTER
   * a payment resolves — every outcome routes somewhere deliberate.
   *
   * `cfg` is the object the SERVER built (key, razorpay order id, amount,
   * prefill). We pass it to Razorpay and pass a result object of our own onward
   * to the success / processing / failed pages — the frontend owns the routing,
   * the backend owns the money.
   */
  private async openRazorpayCheckout(orderPk: number, cfg: any) {
    const ready = await this.loadRazorpayScript();
    if (!ready) {
      this.orderLoading = false;
      this.goToPaymentFailed({
        order_reference: cfg.order_reference,
        amount: this.amountPayable,
        reason: 'We could not load the payment window. Please check your connection and try again.',
        code: 'sdk_load_failed',
      });
      return;
    }

    this.paymentResolved = false;
    this.paymentError = '';

    const options: any = {
      // Publishable, and it came from the server rather than this bundle.
      key: cfg.key,
      // With an order id present, Razorpay charges what THAT order says. The
      // amount below is for display only and cannot override it.
      order_id: cfg.razorpay_order_id,
      amount: cfg.amount,
      currency: cfg.currency || 'INR',
      name: cfg.name || 'Mahatta Art',
      description: `Order ${cfg.order_reference || ''}`.trim(),
      prefill: {
        name: cfg.prefill?.name || '',
        email: cfg.prefill?.email || '',
        contact: cfg.prefill?.contact || '',
      },
      notes: { order_reference: cfg.order_reference || '' },
      theme: { color: '#600b0b' },

      /*
       * Razorpay's own retry OFF, so a failed payment closes the modal and hands
       * control back to us rather than showing its in-modal retry screen. We take
       * the customer to our own failed page, which is the branded outcome the
       * frontend is meant to own.
       */
      retry: { enabled: false },

      // Success. The three values Razorpay returns are NOT believed here — they
      // go to the server, which checks the signature and re-fetches the payment
      // from Razorpay before an order is marked paid.
      handler: (rzp: any) => {
        this.paymentResolved = true;
        this.verifyRazorpayPayment(orderPk, rzp, cfg);
      },

      modal: {
        // The customer closed the window WITHOUT a result. Not a failure — they
        // backed out — so it stays on checkout with the cart intact and "Pay now"
        // ready. Guarded: a dismiss firing after a handled failure/success must
        // not overwrite the navigation already under way.
        ondismiss: () => {
          if (this.paymentResolved) return;
          this.paymentResolved = true;
          this.orderLoading = false;
          this.paymentError = 'Payment was not completed. Your cart is saved — you can try again.';
          this.reportPaymentAbandoned(orderPk, cfg.razorpay_order_id, 'Customer closed the payment window.');
        },
      },
    };

    try {
      const rzp = new (window as any).Razorpay(options);

      // A gateway-side failure (bank declined, UPI timeout, insufficient funds).
      // Reported for the audit trail, then the customer is taken to the failed
      // page with the reason and the order details. The order and cart are left
      // alone so "Try again" simply works.
      rzp.on('payment.failed', (resp: any) => {
        if (this.paymentResolved) return;
        this.paymentResolved = true;
        this.orderLoading = false;

        const reason = resp?.error?.description || 'The payment could not be completed.';
        this.reportPaymentAbandoned(orderPk, cfg.razorpay_order_id, reason);
        try { rzp.close(); } catch { /* modal may already be closing */ }

        this.goToPaymentFailed({
          order_reference: cfg.order_reference,
          amount: this.amountPayable,
          reason,
          code: resp?.error?.code || '',
          payment_id: resp?.error?.metadata?.payment_id || '',
        });
      });

      rzp.open();
    } catch {
      this.orderLoading = false;
      this.goToPaymentFailed({
        order_reference: cfg.order_reference,
        amount: this.amountPayable,
        reason: 'We could not open the payment window. Please try again.',
        code: 'open_failed',
      });
    }
  }

  /**
   * Take the customer to the failed page, carrying the details with them.
   *
   * The object goes two ways on purpose. Router `state` carries the rich detail
   * (reason, code, payment id) — clean, not in the URL. The order reference also
   * goes in a query param, so a refresh of the failed page keeps at least the
   * order it was about when `state` is gone.
   */
  private goToPaymentFailed(details: {
    order_reference?: string;
    amount?: number;
    reason?: string;
    code?: string;
    payment_id?: string;
  }) {
    this.router.navigate(['/payment-failed'], {
      queryParams: {
        order_id: details.order_reference || '',
        // A Buy Now carries its cart rows, so "Try again" returns to the SAME
        // items. Without them the retry opened /checkout — the WHOLE cart — and
        // the next payment billed everything in it.
        ...(this.buyNowMode && this.buyNowItemIds.length ? { items: this.buyNowItemIds.join(',') } : {}),
      },
      state: { payment: { ...details } },
    });
  }

  /**
   * Record that a payment attempt ended without money arriving.
   *
   * Deliberately silent in both directions. It reports something the customer
   * has already seen happen, so a failed report must not put a second error in
   * front of them — and there is nothing to retry, because the order and the cart
   * are untouched either way.
   *
   * If the webhook settled the order while the modal was open, the server says so
   * and marks nothing abandoned; that race is handled there rather than here.
   */
  private reportPaymentAbandoned(orderPk: number, razorpayOrderId: string, reason: string) {
    this.orderService.razorpayCancel({
      order_id: orderPk,
      razorpay_order_id: razorpayOrderId,
      reason,
    }).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /**
   * Have the server confirm the payment, then route by the outcome.
   *
   * Every branch navigates, so the customer never stays on checkout after paying:
   *
   *   paid        -> /thank-you            (cart cleared)
   *   processing  -> /thank-you?processing (money in flight; cart kept)
   *   not settled -> /payment-failed       (genuine non-settlement; cart kept)
   *   verify erred-> /thank-you?processing (ambiguous — never claim failure when
   *                                         the money may in fact have arrived)
   *
   * The cart is cleared ONLY on a confirmed `paid`, and the server has already
   * cleared the real rows by then.
   */
  private verifyRazorpayPayment(orderPk: number, rzp: any, cfg: any) {
    const reference = cfg?.order_reference || '';

    this.orderService.razorpayVerify({
      order_id: orderPk,
      razorpay_order_id: rzp?.razorpay_order_id,
      razorpay_payment_id: rzp?.razorpay_payment_id,
      razorpay_signature: rzp?.razorpay_signature,
    }).subscribe({
      next: (res: any) => {
        this.orderLoading = false;

        if (res?.data?.paid) {
          this.clearCart();
          // The order is placed; the next one must not inherit its site services.
          this.state.forgetPdpServiceChoices();
          this.router.navigate(['/thank-you'], {
            queryParams: { order_id: res.data.order_reference || reference },
          });
          return;
        }

        // `partial`: money WAS captured but less than the order total (an anomaly
        // the server records and our team resolves — the website takes no part-
        // payments). Routed like processing so the customer is never sent to the
        // failed page and invited to pay again.
        if (res?.data?.processing || res?.data?.partial) {
          // Money is in flight — held at the bank, or a UPI/netbanking/NEFT
          // payment being completed. The cart is NOT cleared; the webhook
          // finishes it either way.
          this.state.forgetPdpServiceChoices();
          this.router.navigate(['/thank-you'], {
            queryParams: { order_id: res.data.order_reference || reference, processing: '1' },
          });
          return;
        }

        // Verified, and genuinely not settled. This is a real failure.
        this.goToPaymentFailed({
          order_reference: res?.data?.order_reference || reference,
          amount: this.amountPayable,
          reason: this.checkoutError(res, 'This payment did not complete. Please try again — your cart is saved.'),
          code: 'not_settled',
          payment_id: rzp?.razorpay_payment_id || '',
        });
      },
      error: (err: any) => {
        this.orderLoading = false;
        /*
         * The verification CALL failed — not the payment. The money may well have
         * arrived, so this must NEVER route to the failed page or invite a second
         * payment. Treated as processing: the webhook settles the order
         * independently, and the thank-you page shows "being confirmed".
         */
        this.state.forgetPdpServiceChoices();
        this.router.navigate(['/thank-you'], {
          queryParams: { order_id: reference, processing: '1' },
        });
      },
    });
  }
}
